/*
 * index.js — wiring for /pdf-editor.
 *
 * Loads as a plain script with window globals (like /sign-pdf), NOT as an ES
 * module graph: relative imports do not inherit a `?v=` query, and the
 * sub-modules would then sit in a browser cache for months while the entry
 * point updated. Each file gets its own versioned URL instead.
 *
 * Order of concerns here: open the document, keep it open, and never lose the
 * user's work. Everything else is a tool on top of that.
 */
(function () {
    'use strict';

    var SLUG = 'pdf-editor';

    /* ── telemetry (shared tier-0 helpers; slug comes from the meta tag) ─── */
    function stage(n) { try { window.__stage && window.__stage(n); } catch (e) {} }
    function funnel(n, d) { try { window.__aeFunnel && window.__aeFunnel(n, d || {}); } catch (e) {} }
    function critical(t, d) { try { window.__aeCritical && window.__aeCritical(t, d || {}); } catch (e) {} }
    function diagnostic(d) { try { window.__aeDiagnostic && window.__aeDiagnostic(d); } catch (e) {} }
    function toast(m, k) { try { window.__toast ? window.__toast(m, k) : null; } catch (e) {} }

    function loadScript(src) {
        return new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.src = src;
            s.onload = resolve;
            s.onerror = function () { reject(new Error('failed to load ' + src)); };
            document.head.appendChild(s);
        });
    }

    window.PdfEditor = { init: init };

    function init(cfg) {
        var els = cfg.els;
        var str = cfg.str || {};
        var t = function (k, fallback) { return str[k] || fallback || k; };

        var S = window.PdfSession;
        var G = window.PdfEditorGeom;
        var Fonts = window.PdfTextFont;
        var TL = window.PdfTextLayer;
        var FM = window.PdfEditorForms;

        var srcBytes = null;        // original file bytes, never mutated
        var pdfDoc = null;          // pdf.js document
        var session = null;
        var viewer = null;
        /* Sentry JP (Firefox 155, Windows): «can't access property "selected",
           s is null» — an `input` event on the property panel's colour field
           while no document was open. Firefox restores form-control values on
           reload / back-forward and dispatches `input` for them, hidden panel
           or not; the property and keyboard handlers assumed a live viewer.
           Every "what is selected" question now goes through one accessor
           that answers «nothing» when there is no viewer, and the panel's
           controls opt out of form restoration (autocomplete="off" in the
           blade) — a restored colour for a document that is no longer open is
           not a value, it is noise. */
        function selectedId() { return viewer ? viewer.selected() : null; }
        var imageBytes = {};        // objectId -> {bytes, mime} for export
        var exported = false;
        var busy = false;

        /* ── open ───────────────────────────────────────────────────────── */

        // On the zone, not on `document`: a file picked before this script ran
        // is handed by the zone to a listener bound on IT (tool-uploader.js,
        // block lateListeners); a listener on `document` only hears live events.
        var dropZone = document.getElementById('peDrop');
        if (dropZone) {
            dropZone.addEventListener('tool-uploader:file', function (e) {
                var file = e.detail && e.detail.file;
                if (file) openFile(file);
            });
            dropZone.addEventListener('tool-uploader:clear', function () { reset(); });
        }

        /*
         * Everything that belongs to ONE document, dropped in one place.
         *
         * Page ids restart at p0 in every session, so a map keyed by page id
         * that outlives its document answers for the next one. Measured
         * 27.10.2026 on a second document opened in the same tab («Другой
         * файл»): the contract showed the previous form's fields over its
         * text, find returned the previous document's words and none of its
         * own, and «Изменить текст» covered lines that were not there — RU
         * 23–27.10, 46 of 81 walled sessions had opened a second document in
         * the same tab. Every `var x = {}` of this file is per-document state;
         * PdfEditorForgetsTheDocumentTest keeps the next one in this list.
         *
         * A draft save scheduled for the document being closed dies with it
         * too — the debounced timer used to fire on `session === null`
         * («Другой файл» right after OCR — Sentry TIMBRICA-FRONTEND-K3).
         */
        function forgetDocument() {
            clearTimeout(saveTimer); saveTimer = null;
            if (viewer) { viewer.destroy(); viewer = null; }
            srcBytes = null; pdfDoc = null; session = null; exported = false; docKeyP = null;
            imageBytes = {}; textLines = {}; thumbCache = {}; pageRasters = {};
            formFields = {}; formEdited = {}; externalDocs = {}; externalSeq = 0; lastMatches = [];
            sidewaysPages = {}; sidewaysChecked = {}; sidewaysDismissed = false; sidewaysShown = false; renderSidewaysHint();
            els.formBadge && (els.formBadge.hidden = true);
            els.findCount && (els.findCount.textContent = '');
            // The properties panel describes the document: a title typed for
            // the last one would be written into this one.
            [els.metaTitle, els.metaAuthor, els.metaSubject, els.metaKeywords].forEach(function (el) { if (el) el.value = ''; });
            var limit = window.__toolLimit;
            if (limit && limit.pending) limit.pending(SLUG, null);
            if (limit && limit.clearNote) limit.clearNote(SLUG);   // the wall spoke about «this document»
        }

        function reset() {
            forgetDocument();
            els.workspace && (els.workspace.hidden = true);
            els.uploadWrap && (els.uploadWrap.hidden = false);
        }

        function openFile(file) {
            forgetDocument();
            stage('open_start');
            setBusy(true, t('opening', 'Открываем документ…'));

            /* Sentry TIMBRICA-FRONTEND-HN (Chrome iOS 137 / iOS 26): «Can't find
               variable: FileReader» — на том WebKit конструктора нет. Общий
               примитив FileBytes читает через file.arrayBuffer() и держит
               FileReader лишь запасным путём; страховка лейаута гарантирует,
               что он есть. */
            var FB = window.FileBytes;
            var readP = (FB && typeof FB.arrayBuffer === 'function') ? FB.arrayBuffer(file)
                      : (file && typeof file.arrayBuffer === 'function') ? file.arrayBuffer()
                      : Promise.reject(new Error('no file reader available'));
            readP.then(function (ab) {
                srcBytes = new Uint8Array(ab);
                openBytes(srcBytes, file.name);
            }, function () {
                setBusy(false);
                fail(t('read_failed', 'Не удалось прочитать файл.'), 'read_failed');
            });
        }

        function openBytes(bytes, name) {
            ensurePdfJs().then(function (pdfjsLib) {
                // A copy, because pdf.js transfers the buffer to its worker and
                // would leave `srcBytes` detached — which then fails at export
                // with an empty-file error that looks like a different bug.
                return pdfjsLib.getDocument({
                    data: bytes.slice(),
                    isEvalSupported: false,
                    // Password-protected files are common enough that we ask
                    // rather than fail; the prompt is wired below.
                    password: '',
                }).promise;
            }).then(function (doc) {
                pdfDoc = doc;
                // Un-hide BEFORE building: the viewer sizes pages from
                // container.clientWidth, and a hidden container measures 0, so
                // every page would be built at the 240px floor and silently
                // stay there. Caught 2026-08-10 by a browser test that read the
                // rendered box instead of trusting the screenshot.
                showWorkspace(name);
                return buildSession(doc);
            }).then(function () {
                setBusy(false);
                stage('opened');
                funnel('opened', { pages: session ? S.pageCount(session) : 0 });
                armDocumentLimit();
            }).catch(function (err) {
                setBusy(false);
                handleOpenError(err, bytes);
            });
        }

        /* The single biggest real-world failure of /sign-pdf, straight from
           production telemetry: `pdf_parse_fail` / TypeError. A file that
           pdf.js will not parse must not end the session with a red toast —
           /pdf-repair exists precisely for these, so offer it. */
        function handleOpenError(err, bytes) {
            var name = (err && (err.name || err.message)) || 'unknown';
            if (/PasswordException/i.test(name) || (err && err.code === 1)) {
                askPassword(bytes);
                return;
            }
            critical('pdf_parse_fail', { detail: String(name).slice(0, 80) });
            showRecovery(t('open_failed', 'Не удалось открыть документ.'),
                         t('open_failed_hint', 'Файл может быть повреждён.'));
        }

        function askPassword(bytes) {
            if (!els.passwordDialog) {
                showRecovery(t('encrypted', 'Документ защищён паролем.'), '');
                return;
            }
            els.passwordDialog.hidden = false;
            els.passwordInput && els.passwordInput.focus();
            els.passwordSubmit.onclick = function () {
                var pw = els.passwordInput.value || '';
                els.passwordDialog.hidden = true;
                setBusy(true, t('opening', 'Открываем документ…'));
                ensurePdfJs().then(function (lib) {
                    return lib.getDocument({ data: bytes.slice(), password: pw, isEvalSupported: false }).promise;
                }).then(function (doc) {
                    pdfDoc = doc;
                    showWorkspace();          // before build — see openBytes()
                    return buildSession(doc);
                }).then(function () {
                    setBusy(false); stage('opened_encrypted');
                    armDocumentLimit();
                }).catch(function () {
                    setBusy(false);
                    toast(t('wrong_password', 'Неверный пароль.'), 'error');
                    askPassword(bytes);
                });
            };
        }

        function showRecovery(title, hint) {
            if (!els.recovery) { toast(title, 'error'); return; }
            els.recovery.hidden = false;
            els.recoveryTitle && (els.recoveryTitle.textContent = title);
            els.recoveryHint && (els.recoveryHint.textContent = hint || '');
        }

        function buildSession(doc) {
            var jobs = [];
            for (var i = 1; i <= doc.numPages; i++) jobs.push(doc.getPage(i));
            return Promise.all(jobs).then(function (pages) {
                session = S.create(pages.map(function (p) {
                    var v = p.view;                       // [x0,y0,x1,y1] MediaBox
                    return { width: v[2] - v[0], height: v[3] - v[1], rotate: p.rotate || 0 };
                }));
                viewer = window.PdfEditorViewer.create({
                    container: els.pages, S: S, G: G, session: session, pdfDoc: doc,
                    onSelect: onSelect,
                    onChange: onChange,
                    onCanvasDown: onCanvasDown,
                    onPageRendered: checkSideways,
                });
                viewer.build();
                renderThumbs();
                syncToolbar();
                updateStatus();
                scanForms();
            });
        }

        var _pdfjs = null;
        function ensurePdfJs() {
            if (_pdfjs) return Promise.resolve(_pdfjs);
            return import(cfg.pdfjsUrl).then(function (lib) {
                lib.GlobalWorkerOptions.workerSrc = cfg.pdfjsWorkerUrl;
                _pdfjs = lib;
                return lib;
            }).catch(function (e) {
                critical('pdfjs_load_failed', { msg: String(e && e.message).slice(0, 80) });
                throw e;
            });
        }

        function showWorkspace(name) {
            els.uploadWrap && (els.uploadWrap.hidden = true);
            els.workspace && (els.workspace.hidden = false);
            els.docName && name && (els.docName.textContent = name);

            // Measured on a 390x664 phone: after opening a file the workspace
            // sat 1078px down the page, so the user picked a document and the
            // screen did not visibly change. `block: 'nearest'` scrolls ONLY
            // when the element is off-screen and never yanks the page on
            // desktop, where it already is in view.
            if (els.workspace && els.workspace.scrollIntoView) {
                requestAnimationFrame(function () {
                    try { els.workspace.scrollIntoView({ block: 'nearest' }); } catch (e) {}
                });
            }
        }

        /* ── tools ──────────────────────────────────────────────────────── */

        var currentTool = 'select';
        var draft = null;                   // in-progress shape/ink
        var pendingEdit = null;             // text box armed to open on pointerup

        /* ── which options belong to which tool ──────────────────────────
           The previous revision kept a colour picker and a thickness slider on
           screen permanently, at the full width of the toolbar, for a set of
           tools that mostly ignore both. Options now follow the tool, and a
           tool with nothing to configure shows no row at all. */
        var TOOL_OPTS = {
            select:    [],
            text:      ['color', 'face', 'bold', 'align'],
            edittext:  ['face', 'bold'],
            highlight: ['color'],
            underline: ['color'],
            strike:    ['color'],
            ink:       ['color', 'width'],
            line:      ['color', 'width'],
            arrow:     ['color', 'width'],
            rect:      ['color', 'width'],
            ellipse:   ['color', 'width'],
            whiteout:  ['color'],
            redact:    [],
            newfield:  [],
            newlink:   [],
        };
        var TOOL_HINT = {
            edittext:  'hint_edittext',
            underline: 'hint_underline',
            strike:    'hint_underline',
            redact:    'hint_redact',
            newfield:  'hint_field',
            ink:       'hint_draw',
            line:      'hint_draw',
            arrow:     'hint_draw',
        };

        function faceHasBold(id) {
            if (!Fonts || !Fonts.faces) return false;
            var list = Fonts.faces();
            for (var i = 0; i < list.length; i++) if (list[i].id === id) return !!list[i].boldUrl;
            return false;
        }

        function syncBoldAvailability() {
            if (!els.boldInput) return;
            var ok = faceHasBold(currentFace());
            els.boldInput.disabled = !ok;
            if (!ok) els.boldInput.checked = false;
            var lab = els.boldInput.closest('label');
            if (lab) lab.title = ok ? '' : t('bold_unavailable', '');
        }

        /* Shows the option row for a tool, or for the OCR panel when that is
           what the user opened. Returns nothing; the row hides itself when the
           active tool has no options, so it never occupies space it has not
           earned. */
        var optsMode = null;                 // null = follow the tool, 'ocr' = OCR panel

        function syncOpts() {
            if (!els.opts) return;
            var want = optsMode === 'ocr' ? ['ocrlang'] : (TOOL_OPTS[currentTool] || []);
            var shown = 0;
            Array.prototype.forEach.call(els.opts.querySelectorAll('[data-opt]'), function (n) {
                var on = want.indexOf(n.dataset.opt) >= 0;
                n.hidden = !on;
                if (on) shown++;
            });
            var hintKey = optsMode === 'ocr' ? 'hint_ocr' : TOOL_HINT[currentTool];
            var hint = hintKey ? t(hintKey, '') : '';
            if (els.optHint) els.optHint.textContent = hint;
            els.opts.hidden = !shown && !hint;
            if (optsMode !== 'ocr') syncBoldAvailability();
        }

        /* ── status strip: zoom, page number, page rail ──────────────────── */

        function updateStatus() {
            if (!viewer) return;
            els.zoomVal && (els.zoomVal.textContent = Math.round(viewer.zoom() * 100) + '%');
            var total = viewer.pageCount ? viewer.pageCount() : 0;
            var idx = viewer.pageIndex ? viewer.pageIndex() : 0;
            els.pageTotal && (els.pageTotal.textContent = total);
            els.pageTotalRead && (els.pageTotalRead.textContent = total);
            els.pageNoRead && (els.pageNoRead.textContent = idx || 1);
            if (els.pageNo && document.activeElement !== els.pageNo) {
                els.pageNo.value = idx || 1;
                els.pageNo.max = total || 1;
            }
            els.prevPage && (els.prevPage.disabled = idx <= 1);
            els.nextPage && (els.nextPage.disabled = !total || idx >= total);
            // The strip's number and the highlighted thumbnail come from the
            // same index, so they cannot disagree.
            if (els.thumbs) {
                var id = viewer.currentPageId && viewer.currentPageId();
                Array.prototype.forEach.call(els.thumbs.children, function (n) {
                    n.classList.toggle('is-current', n.dataset.pageId === id);
                });
            }
        }

        function setTool(name) {
            currentTool = name;
            viewer && viewer.setMode(name === 'select' ? 'select' : 'draw');
            Array.prototype.forEach.call(els.toolButtons || [], function (b) {
                b.classList.toggle('is-active', b.dataset.tool === name);
                b.setAttribute('aria-pressed', b.dataset.tool === name ? 'true' : 'false');
            });
            els.pages && (els.pages.dataset.tool = name);
            // Picking a drawing tool means the user left the OCR panel.
            if (optsMode === 'ocr' && name !== 'select') optsMode = null;
            syncOpts();
        }

        function onCanvasDown(pageId, at, ev) {
            if (currentTool === 'select') return;

            if (currentTool === 'text') {
                var id = S.addObject(session, {
                    type: 'text', page: pageId,
                    text: t('new_text', 'Текст'),
                    size: 14, color: '#111111', faceId: currentFace(), bold: currentBold(),
                    align: currentAlign(),
                    placement: { nx: at.nx, ny: at.ny, nw: 0.35, nh: 0.06, rot: 0 },
                });
                onChange();
                viewer.refreshOverlays();
                viewer.select(id);
                setTool('select');
                // Entering edit mode HERE would focus the box during
                // pointerdown, and the click that follows in the same gesture
                // immediately moves focus away — blurring it shut before the
                // user can type a character. So arm it and enter on pointerup,
                // which is deterministic rather than a guessed delay.
                pendingEdit = { id: id, selectAll: true };
                ev.preventDefault();
                return;
            }

            if (currentTool === 'edittext') {
                editExistingText(pageId, at, ev);
                return;
            }

            if (currentTool === 'ink' || currentTool === 'signdraw') {
                draft = { pageId: pageId, points: [[at.nx, at.ny]], kind: 'ink' };
                ev.preventDefault();
                return;
            }

            // Straight-line family. All four are ONE gesture with two
            // outcomes: a drag draws the line the user dragged, and a tap on a
            // text line (underline / strike-through only) snaps to that line's
            // own box. One tool, no mode switch - a scanned page with no text
            // layer still gets an underline by dragging.
            if (STRAIGHT[currentTool]) {
                draft = {
                    pageId: pageId, kind: currentTool, straight: true, start: at,
                    points: [[at.nx, at.ny], [at.nx, at.ny]],
                };
                ev.preventDefault();
                return;
            }

            if (['rect', 'ellipse', 'highlight', 'whiteout', 'redact', 'newfield', 'newlink'].indexOf(currentTool) >= 0) {
                draft = { pageId: pageId, start: at, kind: currentTool };
                ev.preventDefault();
                return;
            }
        }

        // Draft strokes/boxes stream through the same pointer events the viewer
        // uses; keeping them here avoids the viewer knowing about tool state.
        window.addEventListener('pointermove', function (e) {
            if (!draft || !viewer) return;
            var rec = viewer.pageRec(draft.pageId);
            if (!rec) return;
            var r = rec.overlay.getBoundingClientRect();
            var at = { nx: (e.clientX - r.left) / r.width, ny: (e.clientY - r.top) / r.height };
            if (draft.straight) {
                draft.points[1] = [at.nx, at.ny];
                paintDraftInk(rec, arrowPoints(draft));
            } else if (draft.kind === 'ink') {
                draft.points.push([at.nx, at.ny]);
                paintDraftInk(rec, draft.points);
            } else {
                draft.end = at;
                paintDraftBox(rec, draft);
            }
        });

        window.addEventListener('pointerup', function () {
            if (pendingEdit) {
                var toEdit = pendingEdit; pendingEdit = null;
                // Next frame: the click event has finished dispatching by then,
                // so focus sticks instead of being taken back immediately.
                requestAnimationFrame(function () {
                    if (typeof toEdit === 'string') editText(toEdit);
                    else editText(toEdit.id, { selectAll: toEdit.selectAll });
                });
            }
            if (!draft || !viewer) return;
            var d = draft; draft = null;
            clearDraft(viewer.pageRec(d.pageId));

            if (d.straight) {
                var a0 = d.points[0], a1 = d.points[1];
                var travel = Math.sqrt(Math.pow(a1[0] - a0[0], 2) + Math.pow(a1[1] - a0[1], 2));
                // A tap, not a drag. For the two text marks that is the normal
                // way to use them; for a bare line it is a misfire, and a
                // zero-length object is not what the user asked for.
                if (travel < 0.012) {
                    if (d.kind === 'underline' || d.kind === 'strike') markTextLine(d.pageId, d.start, d.kind);
                    return;
                }
                var lpts = arrowPoints(d);
                S.addObject(session, {
                    type: 'line', page: d.pageId, points: lpts,
                    color: currentColor(), strokeWidth: markWidth(d.kind),
                    placement: G.inkBounds(lpts),
                });
                onChange();
                viewer.refreshOverlays();
                setTool('select');
                return;
            }

            if (d.kind === 'ink') {
                if (d.points.length < 2) return;
                var pts = G.simplify(d.points, 0.0015);
                S.addObject(session, {
                    type: 'ink', page: d.pageId, points: pts,
                    color: currentColor(), strokeWidth: currentWidth(),
                    placement: G.inkBounds(pts),
                });
            } else if (d.end) {
                var nx = Math.min(d.start.nx, d.end.nx), ny = Math.min(d.start.ny, d.end.ny);
                var nw = Math.abs(d.end.nx - d.start.nx), nh = Math.abs(d.end.ny - d.start.ny);
                if (nw < 0.005 || nh < 0.005) return;      // a click, not a drag
                var base = { page: d.pageId, placement: { nx: nx, ny: ny, nw: nw, nh: nh, rot: 0 } };
                if (d.kind === 'rect') S.addObject(session, Object.assign(base, { type: 'rect', stroke: currentColor(), strokeWidth: currentWidth() }));
                else if (d.kind === 'ellipse') S.addObject(session, Object.assign(base, { type: 'ellipse', stroke: currentColor(), strokeWidth: currentWidth() }));
                else if (d.kind === 'highlight') S.addObject(session, Object.assign(base, { type: 'highlight', color: currentColor('#ffe600'), opacity: 0.4 }));
                else if (d.kind === 'whiteout') S.addObject(session, Object.assign(base, { type: 'whiteout', fill: '#ffffff' }));
                else if (d.kind === 'newfield') { askNewField(base); return; }
                else if (d.kind === 'newlink') { askNewLink(base); return; }
                else if (d.kind === 'redact') {
                    S.addObject(session, Object.assign(base, { type: 'redact', fill: '#000000' }));
                    warnRedactRasterises();
                }
            }
            onChange();
            viewer.refreshOverlays();
            setTool('select');
        });

        /* ── straight lines, arrows and text marks ──────────────── */

        var STRAIGHT = { line: 1, arrow: 1, underline: 1, strike: 1 };

        function markWidth(kind) {
            // An underline drawn at the freehand thickness looks like a
            // redaction bar. Text marks get their own thin default; the pen
            // tools keep whatever the user set on the slider.
            if (kind === 'underline' || kind === 'strike') return 1.5;
            return currentWidth();
        }

        /* Points for the committed object. A plain line is its two ends; an
           arrow is ONE polyline that draws the shaft and then both head
           strokes, so the draft preview, the editor overlay and the exporter
           all run through the same path writer instead of three that can
           disagree.

           The head is computed in PAGE POINTS, not in the 0..1 placement space:
           a 45-degree drag on an A4 is not 45 degrees once the axes are scaled
           back, and a head built in normalised space comes out visibly skewed. */
        function arrowPoints(d) {
            var a = d.points[0], b = d.points[1];
            if (d.kind !== 'arrow') return [[a[0], a[1]], [b[0], b[1]]];
            var page = S.pageById(session, d.pageId);
            var box = page ? S.displayBox(page) : { width: 595, height: 842 };
            var dx = (b[0] - a[0]) * box.width, dy = (b[1] - a[1]) * box.height;
            var len = Math.sqrt(dx * dx + dy * dy);
            if (len < 1) return [[a[0], a[1]], [b[0], b[1]]];
            var ux = dx / len, uy = dy / len;
            var head = Math.max(6, Math.min(22, len * 0.18));
            var cos = Math.cos(0.42), sin = Math.sin(0.42);      // ~24 degrees
            function wing(sign) {
                var bx = -ux, by = -uy;
                var rx = bx * cos - sign * by * sin;
                var ry = sign * bx * sin + by * cos;
                return [b[0] + (rx * head) / box.width, b[1] + (ry * head) / box.height];
            }
            return [[a[0], a[1]], [b[0], b[1]], wing(1), [b[0], b[1]], wing(-1)];
        }

        /* Tap-to-mark. Reuses the very same line geometry the edit-text tool
           uses, so an underline sits ON the glyphs rather than near them. */
        function markTextLine(pageId, at, kind) {
            linesFor(pageId).then(function (lines) {
                if (!lines.length) {
                    toast(t('no_text_here_mark', ''), 'warning');
                    return;
                }
                var page = S.pageById(session, pageId);
                var hit = TL.lineAt(lines, {
                    width: page.width, height: page.height, rotate: S.effectiveRotate(page),
                }, at);
                if (!hit) { toast(t('no_line_here', ''), 'info'); return; }
                var pl = hit.placement;
                // Underline sits just below the baseline; strike-through runs
                // through the x-height, which is nearer 0.58 of the line box
                // than its middle.
                var fy = pl.ny + pl.nh * (kind === 'underline' ? 1.02 : 0.58);
                var mpts = [[pl.nx, fy], [pl.nx + pl.nw, fy]];
                S.addObject(session, {
                    type: 'line', page: pageId, points: mpts,
                    color: currentColor(), strokeWidth: markWidth(kind),
                    placement: G.inkBounds(mpts),
                });
                onChange();
                viewer.refreshOverlays();
                setTool('select');
            }).catch(function () { toast(t('edit_failed', ''), 'error'); });
        }

        /* ── editing text that is already in the document ───────────────── */

        var textLines = {};          // pageId -> lines, extracted once per page

        function linesFor(pageId) {
            if (textLines[pageId]) return Promise.resolve(textLines[pageId]);
            var page = S.pageById(session, pageId);
            if (!page || page.src === null || !pdfDoc) return Promise.resolve([]);
            return pdfDoc.getPage(page.src + 1).then(function (pdfPage) {
                return pdfPage.getTextContent();
            }).then(function (tc) {
                textLines[pageId] = TL.buildLines(tc.items, tc.styles, {});
                return textLines[pageId];
            }).catch(function () { return []; });
        }

        /*
         * A PDF stores glyphs at coordinates, not paragraphs — there is no
         * reflow to hook into and the embedded font usually lacks the letters
         * the user is about to type. So editing a line means covering the old
         * glyphs and drawing new ones, which is what every online editor does.
         * Doing it WELL is entirely about the measurements: patch the real
         * paper colour, keep the original size, and pick a face that can
         * actually render the replacement.
         */
        function editExistingText(pageId, at, ev) {
            ev && ev.preventDefault();
            linesFor(pageId).then(function (lines) {
                if (!lines.length) {
                    // A dead end with an obvious way out is not a dead end.
                    toast(t('no_text_here_ocr', ''), 'warning');
                    setTool('select');
                    return;
                }
                var page = S.pageById(session, pageId);
                var hit = TL.lineAt(lines, {
                    width: page.width, height: page.height, rotate: S.effectiveRotate(page),
                }, at);
                if (!hit) {
                    toast(t('no_line_here', 'Здесь нет строки. Нажмите прямо по тексту.'), 'info');
                    return;
                }
                if (hit.line.skewed) {
                    toast(t('line_rotated', 'Эта строка повёрнута — её правка не поддерживается.'), 'warning');
                    return;
                }

                var rec = viewer.pageRec(pageId);
                var colors = sampleLine(rec, hit.placement);

                // A line sitting on a rule, a gradient or an image has no single
                // paper colour; a flat patch there is a visible scar. Say so and
                // let the user decide rather than damaging the page quietly.
                if (colors && !colors.confident) {
                    toast(t('bg_not_flat', 'Фон под строкой неоднородный — заплатка будет заметна.'), 'warning');
                    diagnostic({ issue: 'edittext_bg_not_flat' });
                }

                var bg = colors ? TL.toHex(colors.bg) : '#ffffff';
                var fg = colors ? TL.toHex(colors.fg) : '#111111';
                var faceId = TL.pickFace(hit.line, hit.line.text, Fonts) || 'noto';

                // Size in points comes from the item's own transform, so the
                // replacement sits on the same baseline instead of jumping.
                var sizePt = hit.line.size;

                S.commit(session);
                S.addObject(session, {
                    type: 'whiteout', page: pageId, fill: bg,
                    placement: hit.placement,
                });
                var id = S.addObject(session, {
                    type: 'text', page: pageId, text: hit.line.text,
                    size: sizePt, color: fg, faceId: faceId,
                    lineHeight: 1.0,
                    placement: hit.placement,
                });
                onChange();
                viewer.refreshOverlays();
                viewer.select(id);
                setTool('select');
                // NOT `pendingEdit`: that hand-off is consumed by the pointerup
                // of the click that started this, and by the time this promise
                // resolves that pointerup is long gone. The patch appeared and
                // the caret never did — a half-working feature with no error.
                // We are already off the gesture, so open it directly.
                requestAnimationFrame(function () { editText(id); });
                warnCoverIsNotRemoval();
                funnel('edit_existing_text', { size: Math.round(sizePt), face: faceId });
            }).catch(function (err) {
                // Without this the whole feature fails in total silence: a
                // rejected promise here produces no toast, no console error and
                // no telemetry, so "nothing happens when I click the text"
                // becomes unreportable and undebuggable.
                critical('edittext_failed', { msg: String(err && err.message || err).slice(0, 120) });
                toast(t('edit_failed', 'Не удалось открыть строку для правки.'), 'error');
                setTool('select');
            });
        }

        /*
         * A PDF has no "delete these glyphs" operation we can reach from the
         * browser, so an edited line is COVERED, not removed — the original
         * characters stay in the content stream and any text extraction still
         * returns them. Verified on our own export 2026-08-10.
         *
         * For «I changed a typo» that is harmless. For «I removed the salary /
         * the name / the address» it is a leak the user has no way to suspect,
         * because the page looks right. Every online editor shares this
         * property; saying so is the difference between a tool and a trap.
         *
         * Once per session — a warning on every keystroke becomes wallpaper.
         */
        /* Redaction really removes the characters, and the only way to do that
           in a browser is to replace the page with a rendering of itself. The
           page stops being selectable text and becomes an image. That is a real
           cost and the user has to know BEFORE they export, not after. */
        var redactWarned = false;
        function warnRedactRasterises() {
            if (redactWarned) return;
            redactWarned = true;
            toast(t('redact_rasterises',
                'Вымаранная страница станет изображением: текст под полосой исчезнет из файла, но и остальной текст этой страницы перестанет выделяться.'),
                'info');
        }

        var coverWarned = false;
        function warnCoverIsNotRemoval() {
            if (coverWarned) return;
            coverWarned = true;
            toast(t('cover_not_removal',
                'Старый текст закрыт, но остаётся в файле и может быть извлечён. Чтобы удалить насовсем — «Вымарать».'),
                'warning');
        }

        /* Read the paper and ink colours off the RENDERED page. Guessing white
           leaves a bright rectangle on every scan, and scans are exactly the
           documents people need to patch. */
        function sampleLine(rec, placement) {
            if (!rec || !rec.canvas || !rec.canvas.width) return null;
            try {
                var c = rec.canvas;
                var ctx = c.getContext('2d', { willReadFrequently: true });
                var box = {
                    x: placement.nx * c.width,
                    y: placement.ny * c.height,
                    w: placement.nw * c.width,
                    h: placement.nh * c.height,
                };
                var pad = Math.ceil(box.h);
                var sx = Math.max(0, Math.floor(box.x - pad));
                var sy = Math.max(0, Math.floor(box.y - pad));
                var sw = Math.min(c.width - sx, Math.ceil(box.w + pad * 2));
                var sh = Math.min(c.height - sy, Math.ceil(box.h + pad * 2));
                if (sw <= 0 || sh <= 0) return null;
                var img = ctx.getImageData(sx, sy, sw, sh);
                return TL.sampleColors(img, { x: box.x - sx, y: box.y - sy, w: box.w, h: box.h });
            } catch (e) {
                // A tainted canvas cannot be read. Never guess a colour in that
                // case — an unpatched line the user can see is better than a
                // wrong-coloured block they discover after export.
                diagnostic({ issue: 'edittext_canvas_unreadable' });
                return null;
            }
        }

        function paintDraftInk(rec, points) {
            var layer = draftLayer(rec);
            var r = rec.overlay.getBoundingClientRect();
            var d = '';
            points.forEach(function (p, i) {
                d += (i ? 'L' : 'M') + (p[0] * r.width).toFixed(1) + ' ' + (p[1] * r.height).toFixed(1) + ' ';
            });
            layer.innerHTML = '<svg class="pe-draft-svg" viewBox="0 0 ' + r.width + ' ' + r.height + '">' +
                '<path d="' + d + '" fill="none" stroke="' + currentColor() + '" stroke-width="' + currentWidth() +
                '" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        }

        function paintDraftBox(rec, d) {
            var layer = draftLayer(rec);
            var nx = Math.min(d.start.nx, d.end.nx), ny = Math.min(d.start.ny, d.end.ny);
            var nw = Math.abs(d.end.nx - d.start.nx), nh = Math.abs(d.end.ny - d.start.ny);
            layer.innerHTML = '';
            var box = document.createElement('div');
            box.className = 'pe-draft-box pe-draft-' + d.kind;
            box.style.left = (nx * 100) + '%'; box.style.top = (ny * 100) + '%';
            box.style.width = (nw * 100) + '%'; box.style.height = (nh * 100) + '%';
            if (d.kind === 'ellipse') box.style.borderRadius = '50%';
            if (d.kind === 'highlight') { box.style.background = currentColor('#ffe600'); box.style.opacity = '.4'; }
            if (d.kind === 'whiteout') { box.style.background = '#fff'; }
            if (d.kind === 'rect' || d.kind === 'ellipse') box.style.borderColor = currentColor();
            layer.appendChild(box);
        }

        function draftLayer(rec) {
            var l = rec.overlay.querySelector('.pe-draft');
            if (!l) { l = document.createElement('div'); l.className = 'pe-draft'; rec.overlay.appendChild(l); }
            return l;
        }
        function clearDraft(rec) {
            if (!rec) return;
            var l = rec.overlay.querySelector('.pe-draft');
            if (l) l.remove();
        }

        function currentColor(fallback) {
            return (els.colorInput && els.colorInput.value) || fallback || '#e11d48';
        }
        function currentWidth() {
            return parseFloat(els.widthInput && els.widthInput.value) || 2;
        }
        function currentFace() {
            return (els.faceSelect && els.faceSelect.value) || 'noto';
        }
        /* Bold is honoured end to end - the object carries it, the overlay
           renders 700 and the exporter embeds the face's BOLD file - but only
           for a face that ships one. Offering it on a face without a bold file
           would show bold on screen and write regular into the PDF, which is
           the WYSIWYG lie this editor exists to avoid. syncBoldAvailability()
           is what keeps the control honest. */
        function currentBold() {
            return !!(els.boldInput && !els.boldInput.disabled && els.boldInput.checked);
        }
        /* Alignment was implemented in BOTH the overlay and the exporter and
           set by nothing — the same shape as bold. Measured before wiring it up
           (2026-08-20): centre and right land where they should in the produced
           file, so this control is the missing call site and not a new feature. */
        function currentAlign() {
            return (els.alignInput && els.alignInput.value) || 'left';
        }

        /* ── text editing in place ──────────────────────────────────────── */

        function editText(id, opts) {
            opts = opts || {};
            var o = S.objectById(session, id);
            if (!o || o.type !== 'text') return;
            var node = els.pages.querySelector('[data-obj-id="' + id + '"] .pe-obj-text');
            if (!node) return;

            node.setAttribute('contenteditable', 'plaintext-only');
            node.classList.add('is-editing');
            node.focus();
            // A box the user just created holds a placeholder, and typing must
            // REPLACE it — «Текст» + what you typed is not what anyone means.
            // A box with real content gets the caret at the end instead, where
            // select-all would let one keystroke destroy existing text.
            var range = document.createRange();
            range.selectNodeContents(node);
            var sel = window.getSelection();
            if (!opts.selectAll) range.collapse(false);
            sel.removeAllRanges(); sel.addRange(range);

            function finish() {
                node.removeAttribute('contenteditable');
                node.classList.remove('is-editing');
                node.removeEventListener('blur', finish);
                node.removeEventListener('keydown', onKey);
                var text = node.textContent || '';
                if (text !== o.text) {
                    S.updateObject(session, id, { text: text });
                    checkCoverage(id, text);
                    onChange();
                }
                viewer.refreshOverlays();
                viewer.select(id);
            }
            function onKey(e) {
                if (e.key === 'Escape') { e.preventDefault(); node.blur(); }
                e.stopPropagation();          // keep editor shortcuts out of typing
            }
            node.addEventListener('blur', finish);
            node.addEventListener('keydown', onKey);
        }

        /* THE HONEST GATE. If the chosen face cannot render what was typed, say
           so and offer a face that can — rather than exporting blank boxes the
           user only discovers in the downloaded file. */
        function checkCoverage(id, text) {
            if (!Fonts) return;
            var o = S.objectById(session, id);
            if (!o) return;
            Fonts.ready(text).then(function () {
                var miss = Fonts.missing(text, o.faceId || 'noto');
                if (!miss.length) { clearCoverageWarning(id); return; }
                var alt = Fonts.pick(text, null);
                if (alt) {
                    S.updateObject(session, id, { faceId: alt });
                    viewer.refreshOverlays();
                    toast(t('font_switched', 'Шрифт заменён на тот, который поддерживает эти символы.'), 'info');
                    diagnostic({ issue: 'font_auto_switch', to: alt });
                } else {
                    showCoverageWarning(id, miss);
                    diagnostic({ issue: 'font_no_coverage', chars: miss.slice(0, 8).join('') });
                }
            });
        }

        function showCoverageWarning(id, miss) {
            var node = els.pages.querySelector('[data-obj-id="' + id + '"]');
            if (node) node.classList.add('has-missing-glyphs');
            toast(t('font_missing', 'Эти символы не поддерживает ни один доступный шрифт: ') + miss.slice(0, 8).join(' '), 'warning');
        }
        function clearCoverageWarning(id) {
            var node = els.pages.querySelector('[data-obj-id="' + id + '"]');
            if (node) node.classList.remove('has-missing-glyphs');
        }

        /* ── selection / properties ─────────────────────────────────────── */

        function onSelect(obj) {
            if (!els.props) return;
            els.props.hidden = !obj;
            if (!obj) return;
            els.props.dataset.type = obj.type;
            if (els.propColor && (obj.color || obj.stroke || obj.fill)) {
                els.propColor.value = obj.color || obj.stroke || obj.fill || '#000000';
            }
            if (els.propSize && obj.size) els.propSize.value = obj.size;
            if (els.propFace && obj.faceId) els.propFace.value = obj.faceId;
            if (els.propBold) {
                els.propBold.checked = !!obj.bold;
                els.propBold.disabled = !faceHasBold(obj.faceId || currentFace());
            }
            if (els.propAlign) els.propAlign.value = obj.align || 'left';
            if (els.propOpacity) els.propOpacity.value = obj.opacity === undefined ? 1 : obj.opacity;
        }

        function onChange() {
            syncToolbar();
            renderThumbs();
            updateStatus();
            scheduleDraftSave();
            if (FM && Object.keys(formFields).length) renderFormLayer();
        }

        function syncToolbar() {
            if (!session) return;
            els.undoBtn && (els.undoBtn.disabled = !S.canUndo(session));
            els.redoBtn && (els.redoBtn.disabled = !S.canRedo(session));
            var s = S.stats(session);
            els.pageCount && (els.pageCount.textContent = s.pages);
            els.exportBtn && (els.exportBtn.disabled = false);
        }

        /* ── page thumbnails ────────────────────────────────────────────── */

        // Rasterised page previews, cached per (page, rotation). A numbered
        // grey box is not a page strip: on a 40-page scan the user navigates by
        // recognising the page, and numbers alone make that impossible.
        var thumbCache = {};

        function thumbKey(p) { return p.id + ':' + S.effectiveRotate(p); }

        function paintThumb(item, p) {
            var key = thumbKey(p);
            if (thumbCache[key]) { showThumb(item, thumbCache[key]); return; }
            if (p.src === null || !pdfDoc) return;          // inserted blank page
            pdfDoc.getPage(p.src + 1).then(function (pdfPage) {
                var rot = ((S.effectiveRotate(p) % 360) + 360) % 360;
                var v1 = pdfPage.getViewport({ scale: 1, rotation: rot });
                var scale = Math.min(76 / v1.width, 104 / v1.height);
                var vp = pdfPage.getViewport({ scale: scale, rotation: rot });
                var c = document.createElement('canvas');
                c.width = Math.max(1, Math.round(vp.width));
                c.height = Math.max(1, Math.round(vp.height));
                var ctx = c.getContext('2d', { alpha: false });
                ctx.fillStyle = '#fff';
                ctx.fillRect(0, 0, c.width, c.height);
                return pdfPage.render({ canvasContext: ctx, viewport: vp }).promise.then(function () {
                    thumbCache[key] = c.toDataURL('image/jpeg', 0.72);
                    showThumb(item, thumbCache[key]);
                });
            }).catch(function () { /* a page that will not draw keeps its number */ });
        }

        function showThumb(item, url) {
            if (!item.isConnected) return;
            var img = item.querySelector('.pe-thumb-img');
            if (!img) {
                img = document.createElement('img');
                img.className = 'pe-thumb-img';
                img.alt = '';
                img.draggable = false;
                item.insertBefore(img, item.firstChild);
            }
            img.src = url;
        }

        function renderThumbs() {
            if (!els.thumbs || !session) return;
            els.thumbs.innerHTML = '';
            S.visiblePages(session).forEach(function (p, i) {
                var item = document.createElement('div');
                item.className = 'pe-thumb';
                item.dataset.pageId = p.id;
                item.draggable = true;
                item.innerHTML = '<span class="pe-thumb-n">' + (i + 1) + '</span>';
                paintThumb(item, p);

                var acts = document.createElement('div');
                acts.className = 'pe-thumb-acts';
                acts.innerHTML =
                    '<button type="button" data-act="rot" title="' + t('rotate', 'Повернуть') + '" aria-label="' + t('rotate', 'Повернуть') + ' ' + (i + 1) + '">⟳</button>' +
                    '<button type="button" data-act="del" title="' + t('delete_page', 'Удалить страницу') + '" aria-label="' + t('delete_page', 'Удалить страницу') + ' ' + (i + 1) + '">✕</button>';
                item.appendChild(acts);
                els.thumbs.appendChild(item);
            });
        }

        els.thumbs && els.thumbs.addEventListener('click', function (e) {
            var btn = e.target.closest('button[data-act]');
            if (!btn) {
                var thumb = e.target.closest('.pe-thumb');
                if (thumb) scrollToPage(thumb.dataset.pageId);
                return;
            }
            var id = btn.closest('.pe-thumb').dataset.pageId;
            if (btn.dataset.act === 'rot') { S.rotatePage(session, id, 90, G); viewer.refresh(); }
            else if (btn.dataset.act === 'del') {
                if (!S.deletePage(session, id)) {
                    toast(t('cannot_delete_last', 'Нельзя удалить единственную страницу.'), 'warning');
                    return;
                }
                viewer.refresh();
            }
            onChange();
        });

        /* Reorder by drag. Uses the HTML5 drag API on the thumbnail strip only —
           the page canvas keeps pointer events for drawing. */
        var dragThumb = null;
        els.thumbs && els.thumbs.addEventListener('dragstart', function (e) {
            var th = e.target.closest('.pe-thumb');
            if (!th) return;
            dragThumb = th.dataset.pageId;
            e.dataTransfer.effectAllowed = 'move';
        });
        els.thumbs && els.thumbs.addEventListener('dragover', function (e) {
            if (dragThumb) e.preventDefault();
        });
        els.thumbs && els.thumbs.addEventListener('drop', function (e) {
            var th = e.target.closest('.pe-thumb');
            if (!th || !dragThumb) return;
            e.preventDefault();
            var to = Array.prototype.indexOf.call(els.thumbs.children, th);
            S.movePage(session, dragThumb, to);
            dragThumb = null;
            viewer.refresh();
            onChange();
        });

        function scrollToPage(id) {
            var rec = viewer && viewer.pageRec(id);
            // block:'nearest' only — never yank the page to the top.
            if (rec) rec.wrap.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }

        /* ── images & signatures ────────────────────────────────────────── */

        function addImageFile(file) {
            if (!/^image\//.test(file.type)) {
                toast(t('not_an_image', 'Это не изображение.'), 'error');
                return;
            }
            var reader = new FileReader();
            reader.onload = function () {
                var bytes = new Uint8Array(reader.result);
                var mime = /png/i.test(file.type) ? 'image/png' : 'image/jpeg';
                var blob = new Blob([bytes], { type: mime });
                var url = URL.createObjectURL(blob);
                var img = new Image();
                img.onload = function () {
                    var pageId = viewer.currentPageId();
                    var fit = G.fitInto(img.naturalWidth, img.naturalHeight, { nw: 0.4, nh: 0.4 });
                    var id = S.addObject(session, {
                        type: 'image', page: pageId, dataUrl: url,
                        placement: { nx: 0.1, ny: 0.1, nw: fit.nw, nh: fit.nh, rot: 0 },
                    });
                    imageBytes[id] = { bytes: bytes, mime: mime };
                    onChange();
                    viewer.refreshOverlays();
                    viewer.select(id);
                };
                img.onerror = function () { toast(t('image_failed', 'Не удалось прочитать изображение.'), 'error'); };
                img.src = url;
            };
            reader.readAsArrayBuffer(file);
        }

        /* ── export ─────────────────────────────────────────────────────── */

        /* ── a document lying on its side ─────────────────────────────────
           A landscape document fed through a portrait scanner arrives as an
           upright page with the document turned inside the picture (chat 708:
           a certificate read bottom to top, and the person fought the text
           tool instead of turning the page once). Each drawn page is asked
           «upright or sideways» from its pixels (orient.js: 171 of 185 measured
           pages decided, none wrong); the hint offers BOTH turns, because which
           way is what pixels answer badly. Checked once per page and turn, in
           idle time, for the first pages only; dismissing it is final for the
           document. */
        var sidewaysPages = {}, sidewaysChecked = {}, sidewaysDismissed = false, sidewaysShown = false;
        var SIDEWAYS_MAX_PAGES = 40;
        function checkSideways(page, canvas) {
            if (sidewaysDismissed || !session || !page || page.src === null || !window.PdfEditorOrient) return;
            var key = page.id + '|' + (((page.turn || 0) % 360) + 360) % 360;
            if (sidewaysChecked[key]) return;
            if (Object.keys(sidewaysChecked).length >= SIDEWAYS_MAX_PAGES) return;
            sidewaysChecked[key] = true;
            var idle = window.requestIdleCallback || function (f) { return setTimeout(f, 80); };
            idle(function () {
                if (!session || sidewaysDismissed) return;
                var g = window.PdfEditorOrient.grayFromCanvas(canvas, 1000);
                if (!g) { delete sidewaysChecked[key]; return; }   // recycled before we looked — ask on the next render
                var verdict = window.PdfEditorOrient.axis(g.gray, g.w, g.h);
                if (verdict === 'sideways') sidewaysPages[page.id] = true;
                else if (verdict === 'upright') delete sidewaysPages[page.id];
                renderSidewaysHint();
            });
        }
        function sidewaysIds() {
            if (!session) return [];
            return session.order.filter(function (id) { return sidewaysPages[id]; });
        }
        function renderSidewaysHint() {
            var box = els.sidewaysHint;
            if (!box) return;
            var ids = sidewaysDismissed ? [] : sidewaysIds();
            box.hidden = !ids.length;
            if (!ids.length) return;
            var nums = ids.map(function (id) { return session.order.indexOf(id) + 1; });
            els.sidewaysText.textContent = nums.length === 1
                ? t('sideways_one', '').replace(':page', nums[0])
                : t('sideways_many', '').replace(':pages', nums.join(', '));
            if (!sidewaysShown) { sidewaysShown = true; funnel('sideways_shown', { n: ids.length, pages: S.pageCount(session) }); }
        }
        function turnSideways(deg) {
            var ids = sidewaysIds();
            if (!ids.length) return;
            ids.forEach(function (id) { S.rotatePage(session, id, deg, G); delete sidewaysPages[id]; });
            funnel('sideways_turned', { dir: deg === 90 ? 'right' : 'left', n: ids.length });
            viewer.refresh(); renderThumbs(); onChange();
            renderSidewaysHint();
        }

        var WALLED = { walled: true };
        function exportPdf() {
            if (busy || !session) return;
            var limit = window.__toolLimit;
            var ticket = null, srcKey = null;

            stage('export_start');
            setBusy(true, t('exporting', 'Собираем документ…'));

            currentDocKey()
                .then(function (k) {
                    // The unit of work is the DOCUMENT, not the click: editing
                    // the same file all afternoon must cost one run, so the key
                    // is the source file, and a repeat export of it is free.
                    srcKey = k;
                    if (limit && limit.begin) return limit.begin(SLUG, [k]);
                    return null;
                })
                .then(function (tk) {
                    // null from begin() = the day's documents are spent: the
                    // wall is already on screen and the refusal already
                    // reported, the caller only has to stop. Until 2026-10-22
                    // this chain went on and saved the file anyway — 313 of
                    // 313 walled sessions in 20 days still downloaded (845
                    // files), so the cap was a sign, never a limit.
                    if (limit && limit.begin && !tk) throw WALLED;
                    ticket = tk;
                    return rasterizeRedactedPages().then(function (rasters) { pageRasters = rasters; return tk; });
                })
                .then(function (tk) {
                    ticket = tk;
                    var Exporter = window.PdfEditorExport.makeExporter({
                        PDFLib: window.PDFLib,
                        PdfCoords: window.PdfCoords,
                        PdfTextFont: window.PdfTextFont,
                    });
                    return Exporter.build(session, srcBytes, {
                        imageBytes: imageBytes,
                        pageRasters: pageRasters,
                        externalDocs: externalDocs,
                        formValues: collectFormValues(),
                        onFieldError: function (name) {
                            toast(t('field_write_failed', '').replace(':name', name), 'warning');
                            diagnostic({ issue: 'field_write_failed' });
                        },
                        formFaceId: FM ? FM.fontForValues(formEdited, Fonts) : 'noto',
                        onRebuild: function (what) {
                            if (what === 'form') {
                                // Reorganising pages forces a rebuild, and a
                                // rebuilt document cannot carry the original
                                // form. Say it rather than let the fields
                                // vanish from a file the user already trusts.
                                toast(t('form_lost_on_reorder', ''), 'warning');
                                diagnostic({ issue: 'form_lost_on_rebuild' });
                            }
                        },
                        metadata: readMetadata(),
                        onProgress: function (done, total) {
                            setBusy(true, t('exporting', 'Собираем документ…') + ' ' + done + '/' + total);
                        },
                    });
                })
                .then(function (bytes) {
                    if (!els.compressOut || !els.compressOut.checked) return bytes;
                    setBusy(true, t('compressing', ''));
                    return compressBytes(bytes, outName()).then(function (r) {
                        if (!r || !r.bytes || r.bytes.length >= bytes.length) {
                            // The optimizer hands back the original when it
                            // cannot do better. Saying "compressed" then would
                            // be a claim about a file we did not change.
                            if (r) toast(t('compress_no_gain', ''), 'info');
                            return bytes;
                        }
                        var saved = Math.round((1 - r.bytes.length / bytes.length) * 100);
                        toast(t('compressed_by', '').replace(':pct', saved), 'success');
                        funnel('compressed', { pct: saved, before: bytes.length, after: r.bytes.length });
                        return r.bytes;
                    });
                })
                .then(function (bytes) {
                    var blob = new Blob([bytes], { type: 'application/pdf' });
                    // ⚠️ The verdict is the RESOLVED VALUE. __downloadFile
                    // refuses by returning false (over the relay cap, a second
                    // delivery in one gesture, unreadable bytes) and practically
                    // never throws — so the old `call(); exported = true;` was
                    // an assumption, and here it was an expensive one: the next
                    // line DELETES the draft. A refused delivery would have
                    // thrown away someone's contract and told them it was saved.
                    return Promise.resolve(window.__downloadFile(blob, outName())).then(function (ok) {
                        setBusy(false);
                        if (ok === false) {
                            // The primitive has already said why, in the user's
                            // language. Keep the draft — it is now the only copy.
                            stage('export_refused');
                            return;
                        }
                        exported = true;
                        // The work is delivered; the draft has done its job and
                        // keeping someone's contract in storage after that is not
                        // insurance, it is a leftover.
                        dbClear();
                        if (ticket && ticket.commit) ticket.commit();
                        // The file just handed over IS this document: opened
                        // again to fix a typo, it is not a second one.
                        if (ticket && limit.sameUnit) {
                            keyOfBytes(bytes).then(function (k) { if (k) limit.sameUnit(SLUG, srcKey, k); });
                        }
                        stage('exported');
                        try {
                            window.__aeToolComplete && window.__aeToolComplete({
                                pages: S.pageCount(session),
                                objects: session.objects.length,
                                bytes: bytes.length,
                            });
                        } catch (e) {}
                        toast(t('exported', 'Готово — файл сохранён.'), 'success');
                    });
                })
                .catch(function (err) {
                    if (err === WALLED) { setBusy(false); stage('export_walled'); return; }
                    // A failed export must not spend the user's daily run.
                    if (ticket && ticket.abort) ticket.abort();
                    setBusy(false);
                    critical('export_failed', { msg: String(err && err.message).slice(0, 120) });
                    toast(t('export_failed', 'Не удалось собрать документ.'), 'error');
                });
        }

        /*
         * Render every page that carries a redaction, with the bars painted ON
         * the bitmap, and hand the bytes to the exporter to use INSTEAD of the
         * original page. This is what makes "redact" mean removed: the glyphs
         * are not in the output at all, so no extraction can recover them.
         *
         * 200 DPI is the compromise: enough that body text stays crisp in
         * print, low enough that a 20-page contract does not become a 200 MB
         * download.
         */
        var pageRasters = {};
        function rasterizeRedactedPages() {
            if (!session || !pdfDoc) return Promise.resolve({});
            var pages = S.visiblePages(session).filter(function (p) {
                return S.objectsOn(session, p.id).some(function (o) { return o.type === 'redact'; });
            });
            if (!pages.length) return Promise.resolve({});

            var DPI = 200, out = {};
            var chain = Promise.resolve();
            pages.forEach(function (p) {
                chain = chain.then(function () {
                    if (p.src === null) return null;
                    return pdfDoc.getPage(p.src + 1).then(function (pdfPage) {
                        var rot = ((S.effectiveRotate(p) % 360) + 360) % 360;
                        var scale = DPI / 72;
                        var vp = pdfPage.getViewport({ scale: scale, rotation: rot });
                        var c = document.createElement('canvas');
                        c.width = Math.round(vp.width); c.height = Math.round(vp.height);
                        var ctx = c.getContext('2d', { alpha: false });
                        ctx.fillStyle = '#fff';
                        ctx.fillRect(0, 0, c.width, c.height);
                        return pdfPage.render({ canvasContext: ctx, viewport: vp }).promise.then(function () {
                            // Burn the bars in. Everything under them is gone
                            // from the bitmap, and the bitmap is all that ships.
                            // A bar the user TURNED is burnt turned, about its
                            // centre, exactly as the editor shows it (CSS rotate)
                            // — an upright fillRect left the glyphs under the
                            // turned ends in the bitmap, under a vector bar that
                            // only hid them (measured 2026-10-22).
                            S.objectsOn(session, p.id).forEach(function (o) {
                                if (o.type !== 'redact') return;
                                var pl = o.placement;
                                var w = pl.nw * c.width, h = pl.nh * c.height;
                                ctx.save();
                                ctx.translate(pl.nx * c.width + w / 2, pl.ny * c.height + h / 2);
                                if (pl.rot) ctx.rotate(pl.rot * Math.PI / 180);
                                ctx.fillStyle = o.fill || '#000000';
                                ctx.fillRect(-w / 2, -h / 2, w, h);
                                ctx.restore();
                            });
                            return new Promise(function (resolve, reject) {
                                c.toBlob(function (blob) {
                                    if (!blob) { reject(new Error('raster failed')); return; }
                                    blob.arrayBuffer().then(function (buf) {
                                        var box = S.displayBox(p);
                                        out[p.id] = {
                                            bytes: new Uint8Array(buf), mime: 'image/jpeg',
                                            pageW: box.width, pageH: box.height,
                                        };
                                        resolve();
                                    }, reject);
                                }, 'image/jpeg', 0.92);
                            });
                        });
                    });
                });
            });
            return chain.then(function () { return out; });
        }

        /* ── colour, on browsers that have no colour input ──────────────── */

        /*
         * Measured 2026-08-10 on WebKit/iPhone: `<input type="color">` is not
         * supported and the element degrades to `type="text"` — so on an iPhone
         * the colour control was a box asking the user to TYPE a hex code, and
         * the property panel showed a clipped «#1111». The input still holds a
         * value, so nothing errors and nothing looks obviously broken; it is
         * simply unusable with a thumb.
         *
         * The fallback is a swatch row, which on a phone beats a system picker
         * anyway: annotation colours are a short, well-known list.
         */
        var SWATCHES = ['#e11d48', '#111111', '#2563eb', '#16a34a', '#f59e0b', '#ffffff'];

        function colorInputSupported() {
            var probe = document.createElement('input');
            probe.setAttribute('type', 'color');
            return probe.type === 'color';
        }

        function installColorFallback(input) {
            if (!input || input.dataset.peFallback) return;
            input.dataset.peFallback = '1';
            input.hidden = true;

            var wrap = document.createElement('span');
            wrap.className = 'pe-swatches';
            SWATCHES.forEach(function (hex) {
                var b = document.createElement('button');
                b.type = 'button';
                b.className = 'pe-swatch-btn';
                b.style.background = hex;
                b.setAttribute('aria-label', hex);
                b.addEventListener('click', function () {
                    input.value = hex;
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                    sync();
                });
                wrap.appendChild(b);
            });
            function sync() {
                Array.prototype.forEach.call(wrap.children, function (b, i) {
                    b.classList.toggle('is-on', SWATCHES[i].toLowerCase() === String(input.value).toLowerCase());
                });
            }
            sync();
            input.parentNode.insertBefore(wrap, input.nextSibling);
        }

        if (!colorInputSupported()) {
            [els.colorInput, els.propColor].forEach(installColorFallback);
            diagnostic({ issue: 'color_input_unsupported' });
        }

        /* ── OCR: making a scan searchable ──────────────────────────────── */

        /*
         * A scanned page has no text at all — no line to click, nothing to
         * find, nothing to copy. OCR gives it one.
         *
         * The recognised text is written INVISIBLY (opacity 0) on top of the
         * image, which is how a "searchable PDF" has always worked: the picture
         * is what you see, the text layer is what search and copy reach. The
         * alternative — drawing the words visibly — double-prints every line
         * over the scan and looks like a defect.
         *
         * OCR is never exact. So the layer is discoverable in the editor (a
         * faint outline) and every line is editable like any other, instead of
         * being an invisible thing the user must trust blindly.
         */
        var ocrBusy = false;

        /*
         * The language of the DOCUMENT, which is not the language of the
         * interface. Measured 2026-08-10: a Russian contract recognised as
         * English comes back as «AOrOBOP Ne 12 ot 10 aBrycrta» — Cyrillic read
         * as Latin lookalikes, at high confidence. That is worse than failing,
         * because it silently poisons search and copy with plausible nonsense.
         *
         * So the UI locale is only the DEFAULT, and the user can say otherwise.
         * Latin rides along with every non-Latin language: a Russian document
         * nearly always carries names, units and URLs in Latin.
         */
        function defaultOcrLang() {
            var loc = (window.__LOCALE || 'en').slice(0, 2);
            var map = {
                ru: 'rus+eng', uk: 'ukr+eng', en: 'eng', de: 'deu+eng', fr: 'fra+eng',
                es: 'spa+eng', pt: 'por+eng', id: 'ind+eng', ko: 'kor+eng', ar: 'ara+eng',
                tr: 'tur+eng',
            };
            return map[loc] || 'eng';
        }

        function ocrLang() {
            return (els.ocrLang && els.ocrLang.value) || defaultOcrLang();
        }

        /* The shared OCR door (public/js/ocr/ocr-engine.js): the neural
           PP-OCRv5 lane for the scripts it knows, Tesseract (fetched on first
           use from window.__OCR_SCRIPT) for the rest or when the lane cannot
           start. One engine per language stays open for the session — a
           second page costs no second download. */
        var ocrEngine = null, ocrEngineLang = null;
        function ensureOcrEngine() {
            var lang = ocrLang();
            if (ocrEngine && ocrEngineLang === lang) return Promise.resolve(ocrEngine);
            if (ocrEngine) { try { ocrEngine.close(); } catch (e) {} ocrEngine = null; ocrEngineLang = null; }
            if (!window.OcrEngine) return Promise.reject(new Error('OcrEngine missing'));
            return window.OcrEngine.open(lang, {
                slug: 'pdf-editor',
                workerUrl: window.__PPOCR_WORKER_URL || undefined,
                onProgress: function (f) { setBusy(true, t('ocr_running', '') + ' ' + Math.round(f * 100) + '%'); },
            }).then(function (eng) { ocrEngine = eng; ocrEngineLang = lang; return eng; });
        }

        /* Sentry D3 (Huawei Y6 2019, 2 ГБ, Yandex/Android 12): движку отдавали
           ХОЛСТ, а tesseract.js превращает его в Blob сам — canvas.toBlob() — и
           на устройстве без памяти получает null, который уезжает в
           FileReader.readAsArrayBuffer как есть: TypeError мимо нашего промиса,
           «распознаю…» навсегда. Нейросетевой движок за общей дверью берёт
           ImageBitmap — Blob не делается вовсе; запасной Tesseract делает его
           там, где null проверяем (ocr-engine.js), и отказывает честно. Второе —
           размер холста: 200 dpi на A4 это 3.9 Мп, на A3-скане 7.7 Мп, на
           постере десятки, и всё это в 2 ГБ телефона вместе с движком. Бюджет
           пикселей — по памяти устройства, пол — 120 dpi (ниже ~150 точность
           падает, но ноль строк хуже). */
        function ocrPixelBudget() {
            var gb = (window.__memBudget && typeof window.__memBudget.gb === 'function' && window.__memBudget.gb())
                || navigator.deviceMemory || 4;
            return gb <= 2 ? 3.2e6 : (gb <= 4 ? 5e6 : 9e6);
        }
        function ocrScale(w1, h1) {
            var want = 200 / 72, floor = 120 / 72;
            var px = Math.max(1, w1 * h1);
            if (px * want * want <= ocrPixelBudget()) return want;
            return Math.max(floor, Math.sqrt(ocrPixelBudget() / px));
        }
        function ocrCurrentPage() {
            if (ocrBusy || !session || !pdfDoc) return;
            var pageId = viewer.currentPageId();
            var page = S.pageById(session, pageId);
            if (!page || page.src === null) return;

            ocrBusy = true;
            stage('ocr_start');
            setBusy(true, t('ocr_running', ''));

            ensureOcrEngine().catch(function (e) {
                toast(t('ocr_unavailable', ''), 'error');
                throw e;
            }).then(function () {
                return pdfDoc.getPage(page.src + 1);
            }).then(function (pdfPage) {
                // 200 DPI is where line recognition stops guessing on body text;
                // below ~150 accuracy falls off a cliff, above 300 it only costs time.
                var rot = ((S.effectiveRotate(page) % 360) + 360) % 360;
                var vp1 = pdfPage.getViewport({ scale: 1, rotation: rot });
                var scale = ocrScale(vp1.width, vp1.height);
                var vp = pdfPage.getViewport({ scale: scale, rotation: rot });
                var c = document.createElement('canvas');
                c.width = Math.round(vp.width); c.height = Math.round(vp.height);
                var ctx = c.getContext('2d', { alpha: false });
                ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
                return pdfPage.render({ canvasContext: ctx, viewport: vp }).promise
                    .then(function () { return { canvas: c, scale: scale }; });
            }).then(function (img) {
                var canvas = img.canvas;
                /* ⚠️ Движок в воркере может бросить отказ МИМО промиса, который мы
                   ждём (tesseract разбирает ответы воркера в СВОЁМ `onmessage`).
                   Тогда `.catch()` ниже не зовётся, `setBusy` остаётся
                   включённым, и человек смотрит на «распознаю…» бесконечно
                   (Sentry DR: `RuntimeError: null function`, 2 человека).
                   Общий примитив доводит такой отказ до нас; берём только
                   отказы движка, чужие ошибки страницы не трогаем. */
                var ENGINE_FAIL_RE = /RuntimeError|null function|memory access out of bounds|out of memory|abort\(|wasm|inference_failed/i;
                var runOcr = function () {
                    return ocrEngine.recognize(canvas, {
                        onLine: function (l) {
                            if (l && l.total) setBusy(true, t('ocr_running', '') + ' ' + Math.round(l.index / l.total * 100) + '%');
                        },
                    }).then(function (data) {
                        return { data: data, canvas: canvas, scale: img.scale };
                    });
                };
                var dg = window.__detached && window.__detached.guard;
                return dg
                    ? dg(runOcr, { tag: 'ocr', match: function (err, ev) {
                        return ENGINE_FAIL_RE.test(String((err && err.message) || (ev && ev.message) || ''));
                    } })
                    : runOcr();
            }).then(function (out) {
                var lines = (out.data && out.data.lines) || [];
                var W = out.canvas.width, H = out.canvas.height;
                var added = 0;

                S.commit(session);
                lines.forEach(function (ln) {
                    var text = String(ln.text || '').replace(/\s+/g, ' ').trim();
                    if (!text) return;
                    // Both engines report a confidence per line (0–100); below
                    // ~40 the "text" is usually noise from a stamp or a scan
                    // artefact, and adding it makes the document WORSE to search.
                    if (typeof ln.confidence === 'number' && ln.confidence < 40) return;
                    var b = ln.bbox || {};
                    var nw = (b.x1 - b.x0) / W, nh = (b.y1 - b.y0) / H;
                    if (!(nw > 0.001 && nh > 0.001)) return;

                    var faceId = Fonts ? (Fonts.pick(text, 'noto') || 'noto') : 'noto';
                    S.addObject(session, {
                        type: 'text', page: pageId, text: text,
                        // Size from the measured line height, in points.
                        // Пиксели → пункты через ФАКТИЧЕСКИЙ масштаб растра (он
                        // больше не всегда 200 dpi).
                        size: Math.max(4, (b.y1 - b.y0) / out.scale * 0.82),
                        color: '#000000', faceId: faceId, lineHeight: 1.0,
                        opacity: 0, ocr: true,
                        placement: { nx: b.x0 / W, ny: b.y0 / H, nw: nw, nh: nh, rot: 0 },
                    });
                    added++;
                });

                setBusy(false);
                ocrBusy = false;
                viewer.refreshOverlays();
                onChange();
                stage('ocr_done');
                if (added) {
                    toast(t('ocr_done', '').replace(':n', added), 'success');
                } else {
                    toast(t('ocr_empty', ''), 'warning');
                }
                funnel('ocr_page', { lines: added, lang: ocrLang(), engine: (out.data && out.data.engine) || '' });
            }).catch(function (err) {
                setBusy(false);
                ocrBusy = false;
                var tooLarge = !!(err && err.code === 'canvas_to_blob_null');
                critical('ocr_failed', { msg: String(err && err.message).slice(0, 120), raster: tooLarge ? 1 : 0 });
                toast(t(tooLarge ? 'ocr_too_large' : 'ocr_failed', ''), 'error');
            });
        }

        /* ── creating fields and links ──────────────────────────────────── */

        /*
         * A field and a link are real PDF constructs, not drawings: a field has
         * a name and a value the recipient fills; a link is an annotation a
         * reader clicks. A blue rectangle that merely looks like either is the
         * version of this feature that ships and does nothing.
         *
         * ⚠️ No signature FIELD: pdf-lib cannot create one, and offering a box
         * no reader can sign would be a promise we cannot keep. The signature
         * tool draws a real signature instead.
         */
        var pendingRect = null;

        function askNewField(base) {
            pendingRect = base;
            if (!els.newFieldDialog) return;
            els.newFieldDialog.hidden = false;
            els.newFieldName && (els.newFieldName.value = '', els.newFieldName.focus());
            els.newFieldOptions && (els.newFieldOptions.value = '');
            syncFieldDialog();
        }

        function syncFieldDialog() {
            if (!els.newFieldKind || !els.newFieldOptionsRow) return;
            var kind = els.newFieldKind.value;
            var needsOptions = kind === 'dropdown' || kind === 'radio';
            els.newFieldOptionsRow.hidden = !needsOptions;
            if (els.newFieldOptions) {
                // The two read the same box but mean different things: a list
                // takes every choice, a radio button takes the ONE value this
                // button stands for.
                els.newFieldOptions.placeholder = kind === 'radio'
                    ? t('field_option_ph', '') : t('field_options_ph', '');
            }
            if (els.newFieldHint) {
                els.newFieldHint.textContent = kind === 'radio'
                    ? t('new_radio_hint', '') : t('new_field_hint', '');
            }
        }

        function commitNewField() {
            if (!pendingRect) return;
            var name = ((els.newFieldName && els.newFieldName.value) || '').trim();
            if (!name) { toast(t('field_needs_name', ''), 'warning'); return; }

            var kind = (els.newFieldKind && els.newFieldKind.value) || 'text';
            var raw = ((els.newFieldOptions && els.newFieldOptions.value) || '').split(/\s*,\s*/).filter(Boolean);
            var options = (kind === 'dropdown') ? raw : (kind === 'radio' ? raw.slice(0, 1) : []);

            if (kind === 'dropdown' && !options.length) { toast(t('field_needs_options', ''), 'warning'); return; }
            if (kind === 'radio') {
                // A radio group SHARES its name on purpose — that is what makes
                // the buttons exclusive. What must be unique is the option this
                // particular button carries.
                if (!options.length) { toast(t('field_needs_option', ''), 'warning'); return; }
                if (radioOptionTaken(name, options[0])) { toast(t('field_option_taken', ''), 'warning'); return; }
                if (fieldNameTakenByOtherKind(name, 'radio')) { toast(t('field_name_taken', ''), 'warning'); return; }
            } else if (fieldNameTaken(name)) {
                toast(t('field_name_taken', ''), 'warning'); return;
            }

            var id = S.addObject(session, Object.assign({}, pendingRect, {
                type: 'formfield', fieldKind: kind, fieldName: name, fieldOptions: options, fieldValue: '',
            }));
            pendingRect = null;
            els.newFieldDialog.hidden = true;
            onChange();
            viewer.refreshOverlays();
            viewer.select(id);
            funnel('field_created', { kind: kind });
        }

        /* A duplicate name is refused HERE rather than at save: pdf-lib throws
           on the second field, and finding that out after ten minutes of layout
           work is not a fair trade. */
        /* Two buttons of the same question may not stand for the same value —
           the document would have a choice the reader cannot express. */
        function radioOptionTaken(name, option) {
            var n = name.toLowerCase(), v = String(option).toLowerCase();
            return session.objects.some(function (o) {
                return o.type === 'formfield' && o.fieldKind === 'radio'
                    && String(o.fieldName || '').toLowerCase() === n
                    && String((o.fieldOptions || [])[0] || '').toLowerCase() === v;
            });
        }

        /* A radio group may reuse its own name, but never a name already spent
           on a text box or a checkbox — pdf-lib throws on the clash. */
        function fieldNameTakenByOtherKind(name, kind) {
            var n = name.toLowerCase();
            return session.objects.some(function (o) {
                return o.type === 'formfield' && o.fieldKind !== kind
                    && String(o.fieldName || '').toLowerCase() === n;
            });
        }

        function fieldNameTaken(name) {
            var lower = name.toLowerCase();
            var inNew = session.objects.some(function (o) {
                return o.type === 'formfield' && String(o.fieldName || '').toLowerCase() === lower;
            });
            if (inNew) return true;
            var inExisting = false;
            Object.keys(formFields).forEach(function (k) {
                formFields[k].forEach(function (f) {
                    if (String(f.name).toLowerCase() === lower) inExisting = true;
                });
            });
            return inExisting;
        }

        function askNewLink(base) {
            pendingRect = base;
            if (!els.newLinkDialog) return;
            els.newLinkDialog.hidden = false;
            els.newLinkUrl && (els.newLinkUrl.value = 'https://', els.newLinkUrl.focus());
        }

        function commitNewLink() {
            if (!pendingRect) return;
            var url = ((els.newLinkUrl && els.newLinkUrl.value) || '').trim();
            // The same schemes the writer accepts, refused here too so the user
            // is told now instead of quietly getting a rectangle that does
            // nothing.
            if (!/^(https?:\/\/|mailto:|tel:)/i.test(url) || /^https?:\/\/$/i.test(url)) {
                toast(t('link_bad_url', ''), 'warning');
                return;
            }
            var id = S.addObject(session, Object.assign({}, pendingRect, { type: 'link', url: url }));
            pendingRect = null;
            els.newLinkDialog.hidden = true;
            onChange();
            viewer.refreshOverlays();
            viewer.select(id);
            funnel('link_created', {});
        }

        /* ── compress on save ───────────────────────────────────────────── */

        /*
         * Reuses /js/compress-pdf-worker.js — the same engine /compress-pdf
         * ships, with its measured behaviour: images are only re-encoded when
         * their EFFECTIVE dpi exceeds the target, and the whole document is
         * handed back untouched if the "optimised" version comes out bigger.
         * A second compressor here would be a second set of bugs.
         *
         * Off by default. Compression is a trade the user makes deliberately;
         * silently shrinking someone's contract is a quality decision that is
         * not ours.
         */
        function compressBytes(bytes, sourceName) {
            return new Promise(function (resolve) {
                var worker;
                try { worker = new Worker((typeof window!=='undefined'&&window.__jsSrc?window.__jsSrc('/js/compress-pdf-worker.js'):'/js/compress-pdf-worker.js')); }
                catch (e) { resolve(null); return; }

                var settled = false;
                var done = function (v) {
                    if (settled) return;
                    settled = true;
                    try { worker.terminate(); } catch (e) {}
                    resolve(v);
                };
                // Never let compression hold the file hostage: the export is
                // already correct without it.
                var timer = setTimeout(function () {
                    diagnostic({ issue: 'compress_timeout' });
                    done(null);
                }, 120000);

                worker.onmessage = function (ev) {
                    var m = ev.data || {};
                    if (m.type === 'progress') {
                        setBusy(true, t('compressing', '') + ' ' + Math.round(m.pct || 0) + '%');
                    } else if (m.type === 'done') {
                        clearTimeout(timer);
                        done({ bytes: new Uint8Array(m.bytes), stats: m.stats || {} });
                    } else if (m.type === 'error') {
                        clearTimeout(timer);
                        diagnostic({ issue: 'compress_failed', klass: m.klass });
                        done(null);
                    }
                };
                worker.onerror = function () { clearTimeout(timer); done(null); };

                var copy = bytes.slice();
                worker.postMessage({
                    type: 'optimize', bytes: copy.buffer,
                    opts: { sourceName: sourceName, updateMetadata: false },
                }, [copy.buffer]);
            });
        }

        /* ── session recovery ───────────────────────────────────────────── */

        /*
         * Nothing here is stored on a server, so a closed tab used to be lost
         * work — the beforeunload prompt warns, and a warning is not a
         * safety net. The document and every edit are kept in IndexedDB (the
         * source PDF is far past what localStorage holds) and offered back on
         * the next visit.
         *
         * Offered, never restored silently: reopening someone's contract
         * unasked is its own kind of wrong, especially on a shared computer.
         */
        var DB_NAME = 'timbrica-pdf-editor';
        var DB_STORE = 'session';

        function openDb() {
            return new Promise(function (resolve, reject) {
                if (!window.indexedDB) { reject(new Error('no indexeddb')); return; }
                var req = indexedDB.open(DB_NAME, 1);
                req.onupgradeneeded = function () {
                    if (!req.result.objectStoreNames.contains(DB_STORE)) req.result.createObjectStore(DB_STORE);
                };
                req.onsuccess = function () { resolve(req.result); };
                req.onerror = function () { reject(req.error); };
            });
        }

        function dbPut(key, value) {
            return openDb().then(function (db) {
                return new Promise(function (resolve, reject) {
                    var tx = db.transaction(DB_STORE, 'readwrite');
                    tx.objectStore(DB_STORE).put(value, key);
                    tx.oncomplete = function () { resolve(); };
                    tx.onerror = function () { reject(tx.error); };
                });
            });
        }

        function dbGet(key) {
            return openDb().then(function (db) {
                return new Promise(function (resolve, reject) {
                    var tx = db.transaction(DB_STORE, 'readonly');
                    var r = tx.objectStore(DB_STORE).get(key);
                    r.onsuccess = function () { resolve(r.result || null); };
                    r.onerror = function () { reject(r.error); };
                });
            });
        }

        function dbClear() { return dbPut('draft', null).catch(function () {}); }

        var saveTimer = null;
        function scheduleDraftSave() {
            if (!session || !srcBytes) return;
            clearTimeout(saveTimer);
            // Debounced: a drag emits dozens of updates and each one would
            // otherwise copy the whole document into storage.
            saveTimer = setTimeout(function () {
                saveTimer = null;
                if (!session || !srcBytes || !S.isDirty(session)) return;
                // Everything the exporter reads, not only the model: until
                // 27.10.2026 the pictures, signatures and imported pages lived
                // in imageBytes / externalDocs alone, so a restored draft showed
                // a broken picture and SAVED a file without the signature and
                // with a blank page where the import had been — no error, no
                // warning. PdfEditorDraftKeepsWhatTheExportReadsTest holds it.
                dbPut('draft', {
                    at: new Date().toISOString(),
                    name: (els.docName && els.docName.textContent) || 'document.pdf',
                    bytes: srcBytes,
                    state: { pages: session.pages, order: session.order, objects: session.objects, seq: session.seq },
                    formEdited: formEdited,
                    imageBytes: imageBytes,
                    externalDocs: externalDocs,
                    externalSeq: externalSeq,
                    meta: readMetadata(),
                }).catch(function () { diagnostic({ issue: 'draft_save_failed' }); });
            }, 1500);
        }

        function offerDraftRestore() {
            dbGet('draft').then(function (d) {
                if (!d || !d.bytes || !els.restoreBar) return;
                els.restoreBar.hidden = false;
                els.restoreName && (els.restoreName.textContent = d.name || '');
                els.restoreWhen && (els.restoreWhen.textContent = new Date(d.at).toLocaleString());
                els.restoreGo && (els.restoreGo.onclick = function () {
                    els.restoreBar.hidden = true;
                    restoreDraft(d);
                });
                els.restoreDrop && (els.restoreDrop.onclick = function () {
                    els.restoreBar.hidden = true;
                    dbClear();
                    funnel('draft_discarded', {});
                });
                funnel('draft_offered', {});
            }).catch(function () {});
        }

        function restoreDraft(d) {
            forgetDocument();
            srcBytes = d.bytes instanceof Uint8Array ? d.bytes : new Uint8Array(d.bytes);
            setBusy(true, t('opening', ''));
            ensurePdfJs().then(function (lib) {
                return lib.getDocument({ data: srcBytes.slice(), isEvalSupported: false }).promise;
            }).then(function (doc) {
                pdfDoc = doc;
                showWorkspace(d.name);
                return buildSession(doc);
            }).then(function () {
                // Replace the freshly built model with the saved one, so page
                // order, rotations and every object come back exactly.
                if (d.state) {
                    session.pages = d.state.pages;
                    session.order = d.state.order;
                    session.objects = d.state.objects;
                    session.seq = d.state.seq || session.seq;
                }
                if (d.formEdited) formEdited = d.formEdited;
                var lost = restoreDraftParts(d);
                setBusy(false);
                viewer.refresh();
                renderThumbs();
                onChange();
                toast(t('draft_restored', ''), 'success');
                if (lost) toast(t('draft_parts_lost', ''), 'warning');
                funnel('draft_restored', { objects: session.objects.length, lost: lost });
                armDocumentLimit();
            }).catch(function () {
                setBusy(false);
                toast(t('draft_restore_failed', ''), 'error');
                dbClear();
            });
        }

        /* Puts back the bytes the model points at: pictures and signatures
           (and a live blob: URL for the overlay — the one in the saved model
           died with its page), the documents imported pages come from, the
           properties typed in. A draft written before 27.10.2026 holds none of
           them; what cannot come back is taken out rather than saved as
           nothing, and the caller says so. Returns how many were taken out. */
        function restoreDraftParts(d) {
            var saved = d.imageBytes || {}, lost = 0;
            var urlOf = typeof Map === 'function' ? new Map() : null;   // a signature on every page shares one buffer
            session.objects = session.objects.filter(function (o) {
                if (o.type !== 'image' && o.type !== 'signature') return true;
                var rec = saved[o.id];
                if (!rec || !rec.bytes) { lost++; return false; }
                var mime = rec.mime || 'image/png';
                var url = urlOf && urlOf.get(rec.bytes);
                if (!url) {
                    url = URL.createObjectURL(new Blob([rec.bytes], { type: mime }));
                    if (urlOf) urlOf.set(rec.bytes, url);
                }
                imageBytes[o.id] = { bytes: rec.bytes, mime: mime };
                o.dataUrl = url;
                return true;
            });
            externalDocs = d.externalDocs || {};
            externalSeq = d.externalSeq || 0;
            Object.keys(externalDocs).forEach(function (k) {
                var n = parseInt(String(k).slice(1), 10);
                if (n >= externalSeq) externalSeq = n + 1;
            });
            var orphan = session.order.filter(function (id) {
                var p = S.pageById(session, id);
                return p && p.external && !externalDocs[p.external.docKey];
            });
            if (orphan.length && orphan.length < session.order.length) {
                session.order = session.order.filter(function (id) { return orphan.indexOf(id) < 0; });
                lost += orphan.length;
            }
            var m = d.meta || {};
            els.metaTitle && (els.metaTitle.value = m.title || '');
            els.metaAuthor && (els.metaAuthor.value = m.author || '');
            els.metaSubject && (els.metaSubject.value = m.subject || '');
            els.metaKeywords && (els.metaKeywords.value = m.keywords || '');
            return lost;
        }

        /* ── forms ──────────────────────────────────────────────────────── */

        /*
         * A form field is not text on a page: it has a name, a type and a
         * value. Filling one means setting that value, so the document stays a
         * form — re-fillable, and readable by whatever system asked for it.
         * Stamping characters over the box looks identical and leaves the field
         * empty, which is how a "completed" application arrives blank.
         */
        var formFields = {};      // pageId -> [field]
        var formEdited = {};      // name -> value

        function loadFormFields(pageId) {
            if (formFields[pageId]) return Promise.resolve(formFields[pageId]);
            var page = S.pageById(session, pageId);
            if (!page || page.src === null || !pdfDoc || !FM) return Promise.resolve([]);
            return pdfDoc.getPage(page.src + 1).then(function (pdfPage) {
                return pdfPage.getAnnotations();
            }).then(function (anns) {
                formFields[pageId] = FM.describe(anns, {
                    width: page.width, height: page.height, rotate: S.effectiveRotate(page),
                }, TL.pdfToDisplay);
                return formFields[pageId];
            }).catch(function () { return []; });
        }

        function scanForms() {
            if (!session || !FM) return Promise.resolve(0);
            var pages = S.visiblePages(session);
            return Promise.all(pages.map(function (p) { return loadFormFields(p.id); }))
                .then(function (lists) {
                    var total = lists.reduce(function (a, l) { return a + l.length; }, 0);
                    if (total) {
                        renderFormLayer();
                        els.formBadge && (els.formBadge.hidden = false);
                        updateFormBadge();
                        funnel('form_detected', { fields: total });
                    }
                    return total;
                });
        }

        function updateFormBadge() {
            if (!els.formBadge) return;
            var all = [];
            Object.keys(formFields).forEach(function (k) { all = all.concat(formFields[k]); });
            var s2 = FM.summarise(all, formEdited);
            els.formBadge.textContent = t('form_badge', '').replace(':filled', s2.filled).replace(':total', s2.total);
        }

        /* The inputs live in the page overlay so they scale and rotate with the
           page exactly like every other object. */
        function renderFormLayer() {
            S.visiblePages(session).forEach(function (p) {
                var rec = viewer.pageRec(p.id);
                var fields = formFields[p.id];
                if (!rec || !fields || !fields.length) return;
                var layer = rec.overlay.querySelector('.pe-form');
                if (layer) layer.remove();
                layer = document.createElement('div');
                layer.className = 'pe-form';
                rec.overlay.appendChild(layer);

                var box = S.displayBox(p);
                var w = box.width * rec.scale, h = box.height * rec.scale;

                fields.forEach(function (f) {
                    if (f.pushButton) return;
                    var el = buildFieldInput(f);
                    el.className += ' pe-field';
                    el.style.left = (f.placement.nx * w) + 'px';
                    el.style.top = (f.placement.ny * h) + 'px';
                    el.style.width = (f.placement.nw * w) + 'px';
                    el.style.height = (f.placement.nh * h) + 'px';
                    if (f.readOnly) { el.disabled = true; el.title = t('field_readonly', ''); }
                    layer.appendChild(el);
                });
            });
        }

        function buildFieldInput(f) {
            var cur = Object.prototype.hasOwnProperty.call(formEdited, f.name) ? formEdited[f.name] : f.value;
            var el;
            if (f.checkbox) {
                el = document.createElement('input');
                el.type = 'checkbox';
                el.checked = cur && cur !== 'Off' && cur !== 'false';
                el.addEventListener('change', function () {
                    formEdited[f.name] = el.checked ? 'Yes' : 'Off';
                    onFormEdit(f);
                });
            } else if (f.kind === 'choice' && f.options.length) {
                el = document.createElement('select');
                f.options.forEach(function (o) {
                    var opt = document.createElement('option');
                    opt.value = o; opt.textContent = o;
                    if (o === cur) opt.selected = true;
                    el.appendChild(opt);
                });
                el.addEventListener('change', function () { formEdited[f.name] = el.value; onFormEdit(f); });
            } else {
                el = document.createElement(f.multiline ? 'textarea' : 'input');
                if (!f.multiline) el.type = 'text';
                el.value = cur;
                if (f.maxLen) el.maxLength = f.maxLen;
                el.addEventListener('input', function () { formEdited[f.name] = el.value; onFormEdit(f); });
            }
            el.setAttribute('aria-label', f.name);
            el.dataset.field = f.name;
            return el;
        }

        function onFormEdit(f) {
            updateFormBadge();
            syncToolbar();
            // The value must be renderable, or the save throws on a font that
            // cannot draw it — the same trap as everywhere else here.
            var v = formEdited[f.name];
            if (v && Fonts) {
                Fonts.ready(v).then(function () {
                    if (!FM.fontForValues(formEdited, Fonts)) {
                        toast(t('font_missing', '') + String(v).slice(0, 12), 'warning');
                    }
                });
            }
        }

        /* ── inserting and importing pages ───────────────── */

        var externalDocs = {};        // docKey -> original bytes, for export
        var externalSeq = 0;

        function insertBlankPage() {
            if (!session) return;
            var after = viewer.currentPageId();
            var order = session.order.indexOf(after);
            // Match the neighbour's size: a letter-sized blank dropped into an
            // A4 contract is invisible on screen and obvious in print.
            var neighbour = S.pageById(session, after) || S.visiblePages(session)[0];
            var box = neighbour ? S.displayBox(neighbour) : { width: 595, height: 842 };
            S.insertPage(session, { width: box.width, height: box.height }, order + 1);
            viewer.refresh();
            onChange();
            funnel('insert_blank_page', {});
        }

        function importPages(file) {
            if (!file || !session) return;
            setBusy(true, t('importing', ''));
            var reader = new FileReader();
            reader.onerror = function () {
                setBusy(false);
                toast(t('read_failed', ''), 'error');
            };
            reader.onload = function () {
                var bytes = new Uint8Array(reader.result);
                var key = 'x' + (externalSeq++);
                ensurePdfJs().then(function (lib) {
                    return lib.getDocument({ data: bytes.slice(), isEvalSupported: false }).promise;
                }).then(function (doc) {
                    var jobs = [];
                    for (var i = 1; i <= doc.numPages; i++) jobs.push(doc.getPage(i));
                    return Promise.all(jobs).then(function (pages) {
                        externalDocs[key] = bytes;
                        var at = session.order.indexOf(viewer.currentPageId()) + 1;
                        pages.forEach(function (pg, i) {
                            var v = pg.view;
                            S.insertPage(session, {
                                external: { docKey: key, index: i },
                                width: v[2] - v[0], height: v[3] - v[1],
                            }, at + i);
                        });
                        setBusy(false);
                        viewer.refresh();
                        onChange();
                        toast(t('imported_n', '').replace(':n', pages.length), 'success');
                        funnel('import_pages', { pages: pages.length });
                    });
                }).catch(function (err) {
                    setBusy(false);
                    critical('import_failed', { msg: String(err && err.message).slice(0, 100) });
                    toast(t('import_failed', ''), 'error');
                });
            };
            reader.readAsArrayBuffer(file);
        }

        /* ── find & replace ─────────────────────────────────────────────── */

        /* Replacement works line by line, because cover-and-retype can only
           rewrite what it can cover. That is also why a match split across two
           lines is skipped rather than half-applied: rewriting it would need a
           paragraph reflow, and a PDF has no paragraphs. */
        var lastMatches = [];

        function findOptions() {
            return {
                matchCase: !!(els.matchCase && els.matchCase.checked),
                wholeWord: !!(els.wholeWord && els.wholeWord.checked),
            };
        }

        function allPageLines() {
            var pages = S.visiblePages(session);
            return Promise.all(pages.map(function (p) {
                return linesFor(p.id).then(function (lines) { return { pageId: p.id, lines: lines }; });
            }));
        }

        function runFind() {
            var q = (els.findInput && els.findInput.value) || '';
            if (!q || !session) return;
            allPageLines().then(function (pages) {
                lastMatches = TL.findMatches(pages, q, findOptions());
                var total = lastMatches.reduce(function (a, m) { return a + m.count; }, 0);
                els.findCount && (els.findCount.textContent = total
                    ? t('found_n', 'Найдено: :n').replace(':n', total)
                    : t('found_none', 'Ничего не найдено'));
                els.replaceAll && (els.replaceAll.disabled = total === 0);
                if (total && lastMatches[0]) scrollToPage(lastMatches[0].pageId);
                funnel('find', { hits: total });
            });
        }

        function runReplaceAll() {
            var q = (els.findInput && els.findInput.value) || '';
            var r = (els.replaceInput && els.replaceInput.value) || '';
            if (!q || !lastMatches.length) return;
            var opts = findOptions();

            S.commit(session);
            var done = 0;
            lastMatches.forEach(function (m) {
                var page = S.pageById(session, m.pageId);
                if (!page || m.line.skewed) return;
                var newText = TL.replaceInText(m.line.text, q, r, opts);
                if (newText === m.line.text) return;

                var placement = TL.toPlacement(TL.lineBox(m.line), {
                    width: page.width, height: page.height, rotate: S.effectiveRotate(page),
                });
                var rec = viewer.pageRec(m.pageId);
                var colors = rec ? sampleLine(rec, placement) : null;
                var faceId = TL.pickFace(m.line, newText, Fonts) || 'noto';

                S.addObject(session, {
                    type: 'whiteout', page: m.pageId,
                    fill: colors ? TL.toHex(colors.bg) : '#ffffff',
                    placement: placement,
                });
                S.addObject(session, {
                    type: 'text', page: m.pageId, text: newText,
                    size: m.line.size, color: colors ? TL.toHex(colors.fg) : '#111111',
                    faceId: faceId, lineHeight: 1.0, placement: placement,
                });
                done += m.count;
            });

            onChange();
            viewer.refreshOverlays();
            warnCoverIsNotRemoval();
            toast(t('replaced_n', 'Заменено: :n').replace(':n', done), done ? 'success' : 'warning');
            els.replaceAll && (els.replaceAll.disabled = true);
            lastMatches = [];
            funnel('replace_all', { replaced: done });
        }

        /* ── signature ──────────────────────────────────────────────────── */

        /* Draw / type / upload all come from public/js/signature-pad.js — the
           same engine /sign-pdf uses, so a signature made here is identical to
           one made there. A second pad is how two tools start producing
           visibly different signatures from the same hand. */
        var sigPad = null;

        function openSignature() {
            if (!session) return;
            if (!window.SignaturePad) {
                toast(t('sig_unavailable', 'Модуль подписи не загрузился.'), 'error');
                return;
            }
            els.sigModal.hidden = false;
            if (!sigPad) {
                sigPad = window.SignaturePad.create({
                    canvas: els.sigCanvas,
                    host: els.sigHost,
                    color: '#0b1f3a',
                    onChange: function () { els.sigUse.disabled = sigPad.isEmpty(); },
                });
            } else {
                sigPad.clear();
            }
            sigPad.resize();
            els.sigUse.disabled = true;
            switchSigMode('draw');
        }

        function closeSignature() { els.sigModal.hidden = true; }

        function switchSigMode(mode) {
            sigPad && sigPad.setMode(mode);
            Array.prototype.forEach.call(els.sigTabs || [], function (b) {
                b.classList.toggle('is-active', b.dataset.mode === mode);
            });
            Array.prototype.forEach.call(els.sigPanes || [], function (n) {
                n.hidden = n.dataset.mode !== mode;
            });
            if (mode === 'type' && els.sigName) els.sigName.focus();
        }

        /*
         * The same signature on every page is what a multi-page contract
         * actually asks for, and doing it by hand means placing it, then
         * copying it page by page and lining each one up by eye.
         */
        function placeSignatureOnAllPages(bytes, url, img) {
            var pages = S.visiblePages(session);
            var fit = G.fitInto(img.naturalWidth, img.naturalHeight, { nw: 0.30, nh: 0.12 });
            S.commit(session);
            var first = null;
            pages.forEach(function (p) {
                var id = S.addObject(session, {
                    type: 'signature', page: p.id, dataUrl: url,
                    placement: { nx: 0.12, ny: 0.75, nw: fit.nw, nh: fit.nh, rot: 0 },
                });
                // One set of bytes, referenced by every copy — the exporter
                // embeds each object separately, so sharing the buffer keeps a
                // 20-page contract from carrying twenty copies of the image.
                imageBytes[id] = { bytes: bytes, mime: 'image/png' };
                if (!first) first = id;
            });
            return { first: first, count: pages.length };
        }

        function placeSignature() {
            if (!sigPad || sigPad.isEmpty()) {
                toast(t('sig_empty', 'Подпись пустая.'), 'warning');
                return;
            }
            sigPad.exportPngBytes().then(function (bytes) {
                var blob = new Blob([bytes], { type: 'image/png' });
                var url = URL.createObjectURL(blob);
                var img = new Image();
                img.onload = function () {
                    var pageId = viewer.currentPageId();
                    // A squashed signature is the classic giveaway, so the box
                    // keeps the drawing's own aspect ratio.
                    var fit = G.fitInto(img.naturalWidth, img.naturalHeight, { nw: 0.30, nh: 0.12 });
                    var everyPage = els.sigAllPages && els.sigAllPages.checked;
                    var id;
                    if (everyPage) {
                        var r = placeSignatureOnAllPages(bytes, url, img);
                        id = r.first;
                        toast(t('signed_n_pages', '').replace(':n', r.count), 'success');
                    } else {
                        id = S.addObject(session, {
                            type: 'signature', page: pageId, dataUrl: url,
                            placement: { nx: 0.12, ny: 0.75, nw: fit.nw, nh: fit.nh, rot: 0 },
                        });
                        imageBytes[id] = { bytes: bytes, mime: 'image/png' };
                    }
                    closeSignature();
                    onChange();
                    viewer.refreshOverlays();
                    if (id) viewer.select(id);
                    funnel('signature_placed', { mode: sigPad.getMode(), all: !!everyPage });
                };
                img.onerror = function () { toast(t('image_failed', ''), 'error'); };
                img.src = url;
            }).catch(function (err) {
                critical('signature_failed', { msg: String(err && err.message).slice(0, 100) });
                toast(t('image_failed', ''), 'error');
            });
        }

        function collectFormValues() {
            if (!FM) return {};
            var all = [];
            Object.keys(formFields).forEach(function (k) { all = all.concat(formFields[k]); });
            return FM.changedValues(all, formEdited);
        }

        /*
         * The day's unit is the document, named by its BYTES. Until 27.10.2026
         * the key was «name:length», and a browser saving a second copy names
         * it «… (1).pdf» — the editor's own result, opened again, could not be
         * recognised even with the key it was saved under. A SHA-256 of the
         * file names the same document whatever it is called on disk.
         */
        var docKeyP = null;
        function keyOfBytes(bytes) {
            try {
                var subtle = window.crypto && window.crypto.subtle;
                if (!subtle || !bytes) return Promise.resolve(null);
                return subtle.digest('SHA-256', bytes).then(function (buf) {
                    var h = new Uint8Array(buf), s = '';
                    for (var i = 0; i < 16; i++) s += (h[i] < 16 ? '0' : '') + h[i].toString(16);
                    return 'pdf#' + s;
                }, function () { return null; });
            } catch (e) { return Promise.resolve(null); }
        }
        // The key this page used before content keys: a document paid under it
        // today (a tab still running the old script) is still paid.
        function legacyDocKey() {
            return (els.docName && els.docName.textContent || 'doc') + ':' + (srcBytes ? srcBytes.length : 0);
        }
        function currentDocKey() {
            if (!docKeyP) docKeyP = keyOfBytes(srcBytes).then(function (k) { return k || legacyDocKey(); });
            return docKeyP;
        }

        /*
         * Say what saving will cost the moment the document is open, not after
         * the work. Bug report 27.10.2026 (guest, cap 1): the day's document
         * was saved at 11:58, a second one opened at 12:23 under a counter
         * reading «0 из 1» at the bottom edge of the screen, 20 minutes of
         * edits, then «Сохранить» — the wall. RU 23–27.10: 81 walled sessions,
         * 12 of them after 5+ minutes of work. precheck() puts up the same
         * wall the press would, with the Save button already answering;
         * pending() makes «0 из 1» say that a document saved today saves again
         * for nothing.
         */
        function armDocumentLimit() {
            var limit = window.__toolLimit;
            var bytes = srcBytes;
            if (!limit || !bytes) return;
            currentDocKey().then(function (k) {
                if (srcBytes !== bytes || !k) return;            // another document since
                if (limit.sameUnit) limit.sameUnit(SLUG, legacyDocKey(), k);
                if (limit.pending) limit.pending(SLUG, [k]);
                if (limit.precheck) limit.precheck(SLUG, [k]);
            });
        }
        function outName() {
            var base = (els.docName && els.docName.textContent || 'document').replace(/\.pdf$/i, '');
            return base + '-edited.pdf';
        }
        /* Only send metadata the user actually filled in — writing empty strings
           would WIPE a title the source document already had. */
        function readMetadata() {
            if (!els.metaTitle) return null;
            var m = {};
            if (els.metaTitle.value) m.title = els.metaTitle.value;
            if (els.metaAuthor && els.metaAuthor.value) m.author = els.metaAuthor.value;
            if (els.metaSubject && els.metaSubject.value) m.subject = els.metaSubject.value;
            if (els.metaKeywords && els.metaKeywords.value) m.keywords = els.metaKeywords.value;
            return Object.keys(m).length ? m : null;
        }

        function setBusy(on, msg) {
            busy = on;
            if (els.busy) {
                els.busy.hidden = !on;
                if (msg && els.busyText) els.busyText.textContent = msg;
            }
            els.exportBtn && (els.exportBtn.disabled = on);
        }

        function fail(msg, reason) {
            critical(reason || 'error', {});
            toast(msg, 'error');
        }

        /* ── keyboard ───────────────────────────────────────────────────── */

        document.addEventListener('keydown', function (e) {
            if (!session) return;
            if (!viewer) return;   // no viewer — no selection, nothing to refresh, nothing to nudge
            var typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) ||
                         e.target.isContentEditable;
            if (typing) return;

            var meta = e.ctrlKey || e.metaKey;
            var kl = window.__keyLetter ? window.__keyLetter(e) : String(e.key || '').toLowerCase();
            if (meta && kl === 'z' && !e.shiftKey) {
                e.preventDefault(); if (S.undo(session)) { viewer.refresh(); onChange(); }
            } else if (meta && (kl === 'y' || (kl === 'z' && e.shiftKey))) {
                e.preventDefault(); if (S.redo(session)) { viewer.refresh(); onChange(); }
            } else if (e.key === 'Delete' || e.key === 'Backspace') {
                var sel = selectedId();
                if (sel) { e.preventDefault(); S.removeObject(session, sel); viewer.select(null); viewer.refreshOverlays(); onChange(); }
            } else if (meta && kl === 's') {
                // Ctrl+S in an editor means "save my document". Without this the
                // browser offers to save the HTML PAGE instead — the one thing
                // the user certainly did not ask for.
                e.preventDefault(); exportPdf();
            } else if (meta && kl === 'd') {
                e.preventDefault(); duplicateSelected();
            } else if (/^Arrow(Up|Down|Left|Right)$/.test(e.key)) {
                if (nudgeSelected(e.key, e.shiftKey)) e.preventDefault();
            } else if (e.key === 'Escape') {
                if (els.sigModal && !els.sigModal.hidden) { closeSignature(); return; }
                setTool('select'); viewer.select(null);
            }
        });

        var lastNudgeAt = -1e9;
        function nudgeNow() { return (window.performance && performance.now) ? performance.now() : +new Date(); }

        /* Nudging. Placing a signature on a printed line or a field inside a
           printed box is work the mouse is bad at: one pixel of pointer jitter
           is a visible offset on paper. The step is defined in PAGE POINTS and
           converted per page, so it is the same physical distance on an A4 and
           on a slide instead of "some fraction of whatever this page is". */
        function nudgeSelected(key, big) {
            var id = selectedId();
            if (!id) return false;
            var o = S.objectById(session, id);
            if (!o || !o.placement) return false;
            var page = S.pageById(session, o.page);
            if (!page) return false;
            var box = S.displayBox(page);
            var step = big ? 10 : 1;                       // points
            var dx = 0, dy = 0;
            if (key === 'ArrowLeft')  dx = -step / box.width;
            if (key === 'ArrowRight') dx =  step / box.width;
            if (key === 'ArrowUp')    dy = -step / box.height;
            if (key === 'ArrowDown')  dy =  step / box.height;

            var pl = o.placement;
            var next = Object.assign({}, pl, {
                nx: Math.max(-0.5, Math.min(1.5, pl.nx + dx)),
                ny: Math.max(-0.5, Math.min(1.5, pl.ny + dy)),
            });
            var patch = { placement: next };
            // Freehand and lines carry their own points; moving the box alone
            // would leave the stroke behind it.
            if (o.points && o.points.length) {
                patch.points = o.points.map(function (pt) { return [pt[0] + dx, pt[1] + dy]; });
            }
            // updateObject commits by itself, so ONE history entry per burst:
            // nudging twenty times to line something up is one intention, and
            // undoing it should not take twenty presses. A pause (or any other
            // action) starts a new entry.
            var now = nudgeNow();
            var coalesce = (now - lastNudgeAt) < 700;
            lastNudgeAt = now;
            S.updateObject(session, id, patch, coalesce ? { history: false } : undefined);
            viewer.refreshOverlays();
            onChange();
            return true;
        }

        function duplicateSelected() {
            var id = selectedId();
            if (!id) return;
            var o = S.objectById(session, id);
            if (!o) return;
            var copy = JSON.parse(JSON.stringify(o));
            delete copy.id;
            // Offset so the copy is visibly a copy and not hiding underneath.
            var off = 0.02;
            if (copy.placement) {
                copy.placement.nx += off;
                copy.placement.ny += off;
            }
            if (copy.points && copy.points.length) {
                copy.points = copy.points.map(function (pt) { return [pt[0] + off, pt[1] + off]; });
            }
            // A form field's NAME must stay unique, so a duplicate gets its own.
            if (copy.type === 'formfield' && copy.fieldKind !== 'radio') {
                copy.fieldName = String(copy.fieldName || 'field') + '_copy';
            }
            var newId = S.addObject(session, copy);
            viewer.refreshOverlays();
            viewer.select(newId);
            onChange();
        }

        /* Leaving with unsaved edits is the one irreversible mistake here —
           nothing is stored server-side, so a closed tab is lost work. */
        window.addEventListener('beforeunload', function (e) {
            if (session && S.isDirty(session) && !exported) {
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
        });

        /* ── bind UI ────────────────────────────────────────────────────── */

        Array.prototype.forEach.call(els.toolButtons || [], function (b) {
            b.addEventListener('click', function () { setTool(b.dataset.tool); });
        });
        els.undoBtn && els.undoBtn.addEventListener('click', function () { if (S.undo(session)) { viewer.refresh(); onChange(); } });
        els.redoBtn && els.redoBtn.addEventListener('click', function () { if (S.redo(session)) { viewer.refresh(); onChange(); } });
        els.exportBtn && els.exportBtn.addEventListener('click', exportPdf);
        function applyZoom(z) { viewer.setZoom(z); viewer.refreshOverlays(); updateStatus(); }
        els.zoomIn && els.zoomIn.addEventListener('click', function () { applyZoom(viewer.zoom() * 1.25); });
        els.zoomOut && els.zoomOut.addEventListener('click', function () { applyZoom(viewer.zoom() / 1.25); });
        // "Fit width" is zoom 1 by construction - the viewer's base scale IS
        // fit-to-width - so the preset and the readout agree without a second
        // definition of what 100% means.
        els.fitWidth && els.fitWidth.addEventListener('click', function () { applyZoom(1); });
        els.fitPage && els.fitPage.addEventListener('click', function () { applyZoom(viewer.fitPageZoom()); });

        function shownPage() {
            var n = els.pageNo ? parseInt(els.pageNo.value, 10) : 0;
            return n > 0 ? n : (viewer.pageIndex() || 1);
        }
        function step(delta) {
            var n = shownPage() + delta;
            viewer.goToPage(n);
            // The scroll has landed, but the strip is refreshed on the next
            // frame so the measurement sees the settled position rather than
            // the one it just left.
            if (els.pageNo) els.pageNo.value = Math.max(1, Math.min(viewer.pageCount() || 1, n));
            requestAnimationFrame(updateStatus);
        }
        els.prevPage && els.prevPage.addEventListener('click', function () { step(-1); });
        els.nextPage && els.nextPage.addEventListener('click', function () { step(1); });
        els.pageNo && els.pageNo.addEventListener('change', function () {
            var n = parseInt(els.pageNo.value, 10);
            if (n > 0) viewer.goToPage(n);
            updateStatus();
        });
        // Scrolling the stage is what changes the current page; the readout has
        // to follow it or it is decoration. rAF-coalesced, so a fast scroll
        // costs one update per frame instead of one per event.
        if (els.pages) {
            var statusQueued = false;
            els.pages.addEventListener('scroll', function () {
                if (statusQueued) return;
                statusQueued = true;
                requestAnimationFrame(function () { statusQueued = false; updateStatus(); });
            }, { passive: true });
        }
        els.thumbsToggle && els.thumbsToggle.addEventListener('click', function () {
            if (!els.shell) return;
            var off = els.shell.classList.toggle('is-railed-off');
            els.thumbsToggle.setAttribute('aria-pressed', off ? 'false' : 'true');
            // The stage got wider (or narrower): re-lay the pages so fit-width
            // still means fit-width.
            viewer.refresh(); viewer.refreshOverlays(); updateStatus();
        });
        function togglePanel(panel) {
            if (!panel) return;
            var opening = panel.hidden;
            [els.findPanel, els.metaPanel].forEach(function (n) { if (n) n.hidden = true; });
            panel.hidden = !opening;
        }
        els.newFieldKind && els.newFieldKind.addEventListener('change', syncFieldDialog);
        els.newFieldOk && els.newFieldOk.addEventListener('click', commitNewField);
        els.newFieldCancel && els.newFieldCancel.addEventListener('click', function () {
            pendingRect = null; els.newFieldDialog.hidden = true;
        });
        els.newLinkOk && els.newLinkOk.addEventListener('click', commitNewLink);
        els.newLinkCancel && els.newLinkCancel.addEventListener('click', function () {
            pendingRect = null; els.newLinkDialog.hidden = true;
        });
        els.newLinkUrl && els.newLinkUrl.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') { e.preventDefault(); commitNewLink(); }
        });
        els.newFieldName && els.newFieldName.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') { e.preventDefault(); commitNewField(); }
        });

        /* OCR is a deliberate, expensive act: it downloads multi-megabyte
           language data and rewrites the page's text layer. Recognising a
           Russian contract as English produces confident nonsense, so the
           language is shown WITH the button rather than buried in the toolbar
           where it was the widest control on the bar and still got ignored. */
        els.ocrBtn && els.ocrBtn.addEventListener('click', function () {
            optsMode = optsMode === 'ocr' ? null : 'ocr';
            els.ocrBtn.classList.toggle('is-active', optsMode === 'ocr');
            syncOpts();
        });
        els.ocrRun && els.ocrRun.addEventListener('click', function () {
            optsMode = null;
            els.ocrBtn && els.ocrBtn.classList.remove('is-active');
            syncOpts();
            ocrCurrentPage();
        });
        els.insertPageBtn && els.insertPageBtn.addEventListener('click', insertBlankPage);
        els.importBtn && els.importBtn.addEventListener('click', function () { els.importInput.click(); });
        els.importInput && els.importInput.addEventListener('change', function (e) {
            if (e.target.files && e.target.files[0]) importPages(e.target.files[0]);
            e.target.value = '';
        });

        els.findBtn && els.findBtn.addEventListener('click', function () {
            togglePanel(els.findPanel);
            if (!els.findPanel.hidden && els.findInput) els.findInput.focus();
        });
        els.metaBtn && els.metaBtn.addEventListener('click', function () { togglePanel(els.metaPanel); });
        els.findGo && els.findGo.addEventListener('click', runFind);
        els.findInput && els.findInput.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') { e.preventDefault(); runFind(); }
        });
        els.replaceAll && els.replaceAll.addEventListener('click', runReplaceAll);

        els.signBtn && els.signBtn.addEventListener('click', openSignature);
        els.sigCancel && els.sigCancel.addEventListener('click', closeSignature);
        els.sigClear && els.sigClear.addEventListener('click', function () {
            sigPad && sigPad.clear();
            els.sigUse.disabled = true;
        });
        els.sigUse && els.sigUse.addEventListener('click', placeSignature);
        Array.prototype.forEach.call(els.sigTabs || [], function (b) {
            b.addEventListener('click', function () { switchSigMode(b.dataset.mode); });
        });
        els.sigName && els.sigName.addEventListener('input', function () {
            sigPad && sigPad.setOption('typedText', els.sigName.value);
            els.sigUse.disabled = !sigPad || sigPad.isEmpty();
        });
        els.sigUpload && els.sigUpload.addEventListener('change', function (e) {
            var f = e.target.files && e.target.files[0];
            // Отказ разбора картинки — обычное дело (битый файл, чужой
            // формат), и он ОБЯЗАН быть сказан. Раньше обещание отвергалось
            // без обработчика: человек не видел ничего, а в Sentry падало
            // `image_decode_failed` (GM). Соседний /sign-pdf это уже ловил —
            // здесь просто забыли.
            if (f && sigPad) sigPad.loadUploadImage(f).catch(function () {
                toast(t('err_image', 'Не удалось прочитать это изображение.'), 'error');
            });
        });
        els.sigModal && els.sigModal.addEventListener('click', function (e) {
            if (e.target === els.sigModal) closeSignature();   // click the backdrop
        });

        els.imageInput && els.imageInput.addEventListener('change', function (e) {
            if (e.target.files && e.target.files[0]) addImageFile(e.target.files[0]);
            e.target.value = '';
        });
        els.pages && els.pages.addEventListener('dblclick', function (e) {
            var node = e.target.closest('.pe-obj-text');
            if (node) editText(node.closest('.pe-obj').dataset.objId);
        });

        // property edits
        els.propColor && els.propColor.addEventListener('input', function () {
            var id = selectedId(); if (!id) return;
            var o = S.objectById(session, id);
            if (!o) return;   // the selection outlived its object (undo / delete) — Sentry TIMBRICA-FRONTEND-KR
            var patch = {};
            if (o.type === 'text' || o.type === 'highlight' || o.type === 'ink') patch.color = els.propColor.value;
            else if (o.type === 'whiteout') patch.fill = els.propColor.value;
            else patch.stroke = els.propColor.value;
            S.updateObject(session, id, patch); viewer.refreshOverlays(); viewer.select(id); onChange();
        });
        els.propSize && els.propSize.addEventListener('input', function () {
            var id = selectedId(); if (!id) return;
            S.updateObject(session, id, { size: parseFloat(els.propSize.value) || 14 });
            viewer.refreshOverlays(); viewer.select(id); onChange();
        });
        els.propFace && els.propFace.addEventListener('change', function () {
            var id = selectedId(); if (!id) return;
            S.updateObject(session, id, { faceId: els.propFace.value });
            viewer.refreshOverlays(); viewer.select(id);
            var o = S.objectById(session, id);
            if (o && o.text) checkCoverage(id, o.text);
            onChange();
        });
        els.propAlign && els.propAlign.addEventListener('change', function () {
            var id = selectedId(); if (!id) return;
            S.updateObject(session, id, { align: els.propAlign.value });
            viewer.refreshOverlays(); onChange();
        });
        els.propBold && els.propBold.addEventListener('change', function () {
            var id = selectedId(); if (!id) return;
            S.updateObject(session, id, { bold: els.propBold.checked });
            viewer.refreshOverlays(); onChange();
        });
        els.propFace && els.propFace.addEventListener('change', function () {
            // A face swap can take bold away from an object that had it.
            var id = selectedId(); if (!id || !els.propBold) return;
            var ok = faceHasBold(els.propFace.value);
            els.propBold.disabled = !ok;
            if (!ok && els.propBold.checked) {
                els.propBold.checked = false;
                S.updateObject(session, id, { bold: false });
                viewer.refreshOverlays(); onChange();
            }
        });
        els.faceSelect && els.faceSelect.addEventListener('change', syncBoldAvailability);
        // A quarter turn clockwise, the same direction as ⟳ on a page. One press
        // is one undo step; a field or a link stays on a right angle because a
        // PDF can turn those only by right angles (see export widgetPlacement).
        els.sidewaysLeft && els.sidewaysLeft.addEventListener('click', function () { turnSideways(270); });
        els.sidewaysRight && els.sidewaysRight.addEventListener('click', function () { turnSideways(90); });
        els.sidewaysClose && els.sidewaysClose.addEventListener('click', function () {
            sidewaysDismissed = true; renderSidewaysHint();
            funnel('sideways_dismissed', { n: sidewaysIds().length });
        });
        els.propRotate && els.propRotate.addEventListener('click', function () {
            var id = selectedId(); if (!id) return;
            var o = S.objectById(session, id);
            if (!o || !o.placement || o.type === 'ink' || o.type === 'line') return;
            var rot = Math.round(((o.placement.rot || 0) + 90) / 90) * 90 % 360;
            S.updateObject(session, id, { placement: Object.assign({}, o.placement, { rot: rot }) });
            viewer.refreshOverlays(); viewer.select(id); onChange();
            funnel('object_turned', { type: o.type, rot: rot });
        });
        els.propDelete && els.propDelete.addEventListener('click', function () {
            var id = selectedId(); if (!id) return;
            S.removeObject(session, id); viewer.select(null); viewer.refreshOverlays(); onChange();
        });

        // Populate the font picker from the measured coverage table, so a face
        // is offered only when we know what it can render.
        Fonts && Fonts.load().then(function () {
            [els.faceSelect, els.propFace].forEach(function (sel) {
                if (!sel) return;
                sel.innerHTML = '';
                Fonts.faces().forEach(function (f) {
                    var opt = document.createElement('option');
                    opt.value = f.id; opt.textContent = f.label;
                    sel.appendChild(opt);
                });
                sel.value = 'noto';
            });
            syncBoldAvailability();
            // The preview families come from this table (viewer cssFamilyFor):
            // boxes drawn before it arrived used the generic, redraw them now.
            if (viewer && viewer.refreshOverlays) viewer.refreshOverlays();
        }).catch(function () {
            diagnostic({ issue: 'font_table_failed' });
        });

        // On a phone the page rail is a 110px band ABOVE the document, and the
        // editor has to fit one screen or the toolbar scrolls away while the
        // user works (this project ships no sticky bars). So it starts folded
        // there and the status strip's toggle is the way back to it. On a
        // desktop it costs a column nobody misses, so it starts open.
        if (els.shell && els.thumbsToggle &&
            window.matchMedia && window.matchMedia('(max-width: 760px)').matches) {
            els.shell.classList.add('is-railed-off');
            els.thumbsToggle.setAttribute('aria-pressed', 'false');
        }

        setTool('select');
        offerDraftRestore();
        stage('ready');
    }
})();
